/**
 * LLM Profiles Page
 * Admin interface for managing LLM model configurations
 * Mission 33 Sprint 5 - Dynamic Model Configuration
 *
 * Promotion (LLM Profile Promotion sprint, Aug 4, 2026):
 * a profile is built, tested on a named cohort, checked by the readiness gate,
 * and then PROMOTED — one flip of is_default that moves every user onto it.
 * Nothing is copied, so the outgoing profile stays intact as the revert target.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LLMProfileConfigurator } from '../components/admin/LLMProfileConfigurator';
import { CostModelingWorkspace } from '../components/admin/CostModelingWorkspace';
import {
  Plus, Users, Settings, Check, X,
  AlertTriangle, CheckCircle, RefreshCw, Rocket, History, RotateCcw, ShieldCheck
} from 'lucide-react';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/apiConfig';

const LLM_PROFILES_API = '/api/admin/llm-profiles';

interface LLMProfile {
  profile_name: string;
  display_name: string;
  description: string;
  is_default: boolean;
  is_system: boolean;
  active?: boolean;
  user_count: number;
  created_at: string;
  created_by: string;
}

// --- Promotion API shapes (services/llm_profile_promotion.py) ---

interface ReadinessIssue {
  kind: string;
  severity: 'block' | 'warn';
  call_name: string | null;
  model_name: string | null;
  detail: string;
  fix_action: string | null;
  /**
   * Optional, and read through several aliases below because the field is
   * server-side and newer than this file. When present it lets a
   * `raise_max_tokens` fix be sent scoped to ONE call at a stated target
   * instead of rewriting the whole profile. Absent = the server picks.
   */
  recommended_max_tokens?: number | null;
  suggested_max_tokens?: number | null;
  min_max_tokens?: number | null;
  safe_max_tokens?: number | null;
}

interface ProbeResult {
  ok: boolean;
  skipped?: boolean;
  detail?: string;
}

/**
 * Who a promotion reaches.
 *
 * Deliberately open-shaped. The server composes this from several pin classes
 * (auto-pinned at signup, unpinned, cohort-pinned, auto-pinned at a third
 * profile) and that composition is expected to keep changing as pin handling is
 * refined — a closed interface here would silently drop whichever component was
 * added last, which is the exact failure the blast-radius number exists to
 * prevent. `users_affected` is a scalar in the shipped shape and may arrive as a
 * composed object; both are read through `impactTotal`.
 */
interface ImpactBreakdown {
  unpinned?: number;
  pinned_to_live?: number;
  pinned_to_candidate?: number;
  auto_pinned_elsewhere?: number;
  users_affected?: number | Record<string, any>;
  [key: string]: any;
}

interface ReadinessReport {
  ready: boolean;
  issues: ReadinessIssue[];
  summary: {
    blocking_count: number;
    warning_count: number;
    live_active_calls: number;
    candidate_active_calls: number;
    missing_calls: number;
    reasoning_floor_violations: number;
    models_probed: number;
    probe_failures: number;
    fallback_call_names_known: number;
  };
  live_profile: string | null;
  candidate_profile: string;
  candidate_is_live: boolean;
  diff_summary: {
    model_changes: Array<{ call_name: string; from: string; to: string }>;
    param_changes: Array<{ call_name: string; field: string; from: any; to: any }>;
    calls_only_in_live: string[];
    calls_only_in_candidate: string[];
    counts: {
      model_changes: number;
      param_changes: number;
      live_calls: number;
      candidate_calls: number;
    };
  };
  impact: ImpactBreakdown;
  /**
   * Projected $/month change from the diff, per SOW WS2.
   *
   * Deliberately `any`: this arrives from a newer server than some builds of
   * this page, and a closed interface would drop whichever field was added
   * last. Everything is read through `normalizeCostDelta`, which returns null
   * when nothing usable is present — rendered as "not available", never as
   * "no change".
   */
  cost_delta?: any;
  projected_cost_delta?: any;
  probe_results: Record<string, ProbeResult>;
  probes_run: boolean;
  /**
   * The probe evidence promote() will actually read, inside its lock.
   *
   * `probes_run` only says "this page asked for probes". This says what got
   * PERSISTED — and promote refuses on absent / older than 60 minutes /
   * fingerprint mismatch, so an admin needs to see it BEFORE the 409 rather
   * than after. Absent on a ?probe=false check, and on any response from a
   * build that predates the probe-evidence table.
   */
  probe_run?: {
    probe_run_id: string;
    model_fingerprint: string;
    probed_at: string;
    probed_by?: string;
  } | null;
}

/** Server-side limit in services/llm_profile_promotion.PROBE_MAX_AGE_MINUTES. */
const PROBE_MAX_AGE_MINUTES = 60;

/**
 * How far the revert target has drifted from the configuration that was live.
 *
 * Revert flips the pointer back; it does NOT restore the profile's rows. If the
 * outgoing profile was edited after being demoted, reverting lands on the edited
 * version — so this has to be on screen before the admin confirms.
 */
interface RevertDrift {
  calls_changed: number | null;
  /** Which calls differ. Empty when the server states a count but no names. */
  changed_calls: string[];
  issues: ReadinessIssue[];
  ready: boolean | null;
  detail: string | null;
}

/** One member of a candidate's test cohort, from the roster endpoint. */
interface CohortMember {
  email: string;
  assigned_at: string | null;
  assigned_by: string | null;
}

interface PromotionEntry {
  seq: number;
  action: string;
  idempotency_key: string;
  from_profile: string | null;
  to_profile: string | null;
  pins_deleted: number;
  users_affected: number | Record<string, any>;
  promoted_by: string;
  created_at: string | null;
  replayed?: boolean;
  // Present only if the server attaches drift to the ledger row. Older rows,
  // written before drift tracking, carry nothing — the dialog falls back to a
  // preview fetch and then to an honest "not available".
  revert_drift?: any;
  drift?: any;
}

interface CohortResult {
  mode: string;
  requested: number;
  matched_users: number;
  rows_changed: number;
  not_found: string[];
}

// ============================================
// SHARED HELPERS
// ============================================

/**
 * Every admin call on this page goes through here.
 *
 * The URL is built from API_BASE_URL, NOT left bare-relative. A relative
 * /api/admin/... resolves against whichever origin is serving the portal and
 * fails two different ways: app.coachcedar.com answers with a redirect loop,
 * and the Firebase origin answers 200 text/html (the SPA fallback) so
 * `response.ok` is true and the call silently does nothing. API_BASE_URL is ''
 * on localhost, so this stays proxy-friendly in dev.
 */
async function adminFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await storage.getItem('admin_token');
  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Authorization': `Bearer ${token || 'admin-token'}`,
      'Content-Type': 'application/json',
      ...(init.headers || {})
    },
    credentials: 'include'
  });
}

/**
 * Pull a usable message out of a FastAPI error body.
 * `detail` is a string (400), an object with {message, issues} (409 readiness
 * block), or a validation array (422).
 */
async function readError(
  response: Response
): Promise<{ message: string; issues: ReadinessIssue[] }> {
  try {
    const data = await response.json();
    const detail = data?.detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      return {
        message: detail.message || `Request failed (${response.status})`,
        issues: Array.isArray(detail.issues) ? detail.issues : []
      };
    }
    if (Array.isArray(detail)) {
      return { message: detail.map((d: any) => d?.msg || String(d)).join('; '), issues: [] };
    }
    if (typeof detail === 'string' && detail) {
      return { message: detail, issues: [] };
    }
  } catch {
    // fall through to the status line
  }
  return { message: `Request failed (${response.status} ${response.statusText})`, issues: [] };
}

/**
 * Stable per-attempt promotion key. Generated once when the confirm dialog
 * opens and reused for every retry, so a network error followed by a second
 * click replays the SAME promotion instead of performing a second one.
 */
function newIdempotencyKey(): string {
  const c: any = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return `promote-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * ARCHIVED is deliberately absent.
 *
 * `GET /api/admin/llm-profiles/profiles` filters `WHERE lmp.active = true`
 * server-side, so a profile with `active = false` never reaches this page. The
 * branch that rendered an ARCHIVED badge could therefore never execute — dead
 * code asserting a state the UI cannot show. It was removed rather than made
 * reachable, because reaching it needs the list endpoint to serve archived rows
 * (an `?include_archived=` parameter that does not exist), and a "show
 * archived" toggle over an endpoint that cannot answer would be a control that
 * silently does nothing.
 *
 * If archived profiles are ever served here, restore the branch — and keep the
 * `is_default` check first: is_default is what the runtime resolver reads, so a
 * profile carrying it is serving every user whatever its active bit says.
 */
type Lifecycle = 'LIVE' | 'TESTING' | 'DRAFT';

function lifecycleOf(profile: LLMProfile): Lifecycle {
  if (profile.is_default) return 'LIVE';
  if ((profile.user_count || 0) > 0) return 'TESTING';
  return 'DRAFT';
}

const LIFECYCLE_STYLE: Record<Lifecycle, { bg: string; color: string; border: string }> = {
  LIVE: { bg: '#E8F2ED', color: '#113C2B', border: '#1F6C4F' },
  TESTING: { bg: '#FEF3C7', color: '#92400E', border: '#D97706' },
  DRAFT: { bg: '#F3F4F6', color: '#4B5563', border: '#D1D5DB' }
};

function LifecycleBadge({ profile }: { profile: LLMProfile }) {
  const state = lifecycleOf(profile);
  const style = LIFECYCLE_STYLE[state];
  const label = state === 'TESTING'
    ? `TESTING · ${profile.user_count} user${profile.user_count === 1 ? '' : 's'}`
    : state;
  return (
    <span
      className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded"
      style={{ backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}` }}
    >
      {state === 'LIVE' && <ShieldCheck className="h-3 w-3 mr-1" />}
      {label}
    </span>
  );
}

const FIX_LABELS: Record<string, string> = {
  import_from_live: 'Import from live',
  match_active: 'Match live active-state',
  raise_max_tokens: 'Raise to safe minimum'
};

/** Label for the same fix narrowed to one call. */
const FIX_LABELS_SCOPED: Record<string, string> = {
  import_from_live: 'Import this call',
  match_active: 'Match this call',
  raise_max_tokens: 'Raise this call'
};

/**
 * Identifies which button is mid-flight. The profile-wide and per-call variants
 * of one fix are distinct actions, so a shared key would spin both and let a
 * click on one disable the other for the wrong reason.
 */
function fixKeyOf(fixAction: string, callName?: string | null): string {
  return callName ? `${fixAction}::${callName}` : fixAction;
}

/**
 * The target `max_tokens` an issue recommends, if it states one. Read through
 * aliases because the field is server-side and newer than this file; absent is
 * fine — the server picks its own safe minimum.
 */
function recommendedMaxTokens(issue: ReadinessIssue): number | null {
  return firstNumber([
    issue.recommended_max_tokens,
    issue.suggested_max_tokens,
    issue.min_max_tokens,
    issue.safe_max_tokens,
    (issue as any).required_max_tokens,
    (issue as any).floor_max_tokens
  ]);
}

// ============================================
// IMPACT (who a promotion reaches)
// ============================================

/**
 * Component labels. Each says what happens to that group's PIN, because that is
 * what decides whether they actually move.
 *
 * An unknown key is still rendered — de-underscored rather than dropped. A
 * component the UI has not heard of is exactly the one an admin most needs to
 * see, and silently omitting it is how a blast-radius number starts lying.
 */
const IMPACT_COMPONENT_LABELS: Record<string, string> = {
  unpinned: 'with no pin — already resolve through the default',
  pinned_to_live: 'pinned to live (pin is deleted by the promotion)',
  pinned_to_live_auto: 'auto-pinned to live at signup — pin is deleted, they move',
  pinned_to_live_admin:
    'deliberately pinned to live by an admin — that pin is NOT deleted, so they stay on the outgoing profile',
  auto_pinned_to_live: 'auto-pinned to live at signup — pin is deleted, they move',
  cohort_pinned_to_live: 'cohort-pinned to live — pin is deleted, they move',
  pinned_to_candidate: 'already pinned to this candidate (pin deleted as redundant)',
  cohort_pinned: 'cohort-pinned to this candidate (pin deleted as redundant)',
  cohort_pinned_to_candidate: 'cohort-pinned to this candidate (pin deleted as redundant)',
  auto_pinned_to_candidate: 'auto-pinned to this candidate (pin deleted as redundant)',
  auto_pinned_elsewhere: 'auto-pinned to a third profile — those pins are deleted too, so they move'
};

/** Preferred display order. Anything unknown is appended after these. */
const IMPACT_COMPONENT_ORDER = [
  'unpinned',
  'pinned_to_live',
  'pinned_to_live_auto',
  'pinned_to_live_admin',
  'auto_pinned_to_live',
  'cohort_pinned_to_live',
  'pinned_to_candidate',
  'cohort_pinned',
  'cohort_pinned_to_candidate',
  'auto_pinned_to_candidate',
  'auto_pinned_elsewhere'
];

/** Keys that carry the total rather than a component of it. */
const IMPACT_TOTAL_KEYS = new Set([
  'users_affected', 'total', 'total_affected', 'affected', 'count', 'users'
]);

/**
 * Keys that are a SUBSET of a component, or not a user bucket at all. Listing
 * them beside the components would double-count the blast radius on screen.
 * They are surfaced separately where they carry their own meaning.
 */
const IMPACT_NON_COMPONENT_KEYS = new Set([
  'pins_deleted_estimate',
  'cohort_pins_on_candidate'
]);

/**
 * key -> the finer split that supersedes it. When the server publishes both a
 * roll-up and its parts, show the parts: the parts are what say who moves.
 */
const IMPACT_SUPERSEDED_BY: Record<string, string[]> = {
  pinned_to_live: ['pinned_to_live_auto', 'pinned_to_live_admin'],
  pinned_to_candidate: ['cohort_pinned_to_candidate', 'auto_pinned_to_candidate']
};

function isFiniteNumber(value: any): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Flatten whichever shape arrived. `users_affected` is a scalar today; if it
 * becomes a composed object its members are merged up so the same renderer
 * handles both without a version check.
 */
function impactSource(impact: ImpactBreakdown | null | undefined): Record<string, any> {
  if (!impact || typeof impact !== 'object') return {};
  const composed = impact.users_affected;
  if (composed && typeof composed === 'object' && !Array.isArray(composed)) {
    return { ...impact, ...composed };
  }
  return impact;
}

function impactComponents(
  impact: ImpactBreakdown | null | undefined
): Array<{ key: string; label: string; value: number }> {
  const source = impactSource(impact);

  const keys = Object.keys(source).filter(key => {
    if (!isFiniteNumber(source[key])) return false;
    if (IMPACT_TOTAL_KEYS.has(key)) return false;
    if (IMPACT_NON_COMPONENT_KEYS.has(key)) return false;
    const finer = IMPACT_SUPERSEDED_BY[key];
    if (finer && finer.some(part => isFiniteNumber(source[part]))) return false;
    return true;
  });

  keys.sort((a, b) => {
    const ia = IMPACT_COMPONENT_ORDER.indexOf(a);
    const ib = IMPACT_COMPONENT_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return keys.map(key => ({
    key,
    label: IMPACT_COMPONENT_LABELS[key] || key.replace(/_/g, ' '),
    value: source[key] as number
  }));
}

/**
 * Groups counted in the total that will NOT actually move — today, users an
 * admin deliberately pinned to the outgoing profile, whose pin the promotion's
 * DELETE does not match. The server over-reports on purpose (the safe direction
 * for a confirmation prompt); saying so is what makes it honest rather than
 * merely conservative.
 */
function impactStayingPut(impact: ImpactBreakdown | null | undefined): number {
  const source = impactSource(impact);
  return ['pinned_to_live_admin', 'admin_pinned_to_live', 'cohort_pinned_to_live_retained']
    .map(key => (isFiniteNumber(source[key]) ? (source[key] as number) : 0))
    .reduce((sum, value) => sum + value, 0);
}

/** Server's own estimate of how many pin rows the promotion deletes. */
function impactPinsDeletedEstimate(impact: ImpactBreakdown | null | undefined): number | null {
  const source = impactSource(impact);
  return isFiniteNumber(source.pins_deleted_estimate) ? source.pins_deleted_estimate : null;
}

/**
 * The blast-radius number. Prefers whatever total the server states; only sums
 * the components when it states none, so a server-side recount always wins over
 * arithmetic done in the browser.
 */
function impactTotal(impact: ImpactBreakdown | null | undefined): number {
  if (!impact || typeof impact !== 'object') return 0;

  const composed = impact.users_affected;
  if (isFiniteNumber(composed)) return composed;
  if (composed && typeof composed === 'object' && !Array.isArray(composed)) {
    for (const key of ['total', 'total_affected', 'users_affected', 'affected', 'count']) {
      if (isFiniteNumber((composed as any)[key])) return (composed as any)[key];
    }
  }
  for (const key of ['total_affected', 'total']) {
    if (isFiniteNumber((impact as any)[key])) return (impact as any)[key];
  }
  return impactComponents(impact).reduce((sum, part) => sum + part.value, 0);
}

/** A ledger row's user count, tolerating the composed shape. */
function countOf(value: number | Record<string, any> | null | undefined): number {
  if (isFiniteNumber(value)) return value;
  if (value && typeof value === 'object') return impactTotal({ users_affected: value } as ImpactBreakdown);
  return 0;
}

function firstNumber(candidates: any[]): number | null {
  for (const candidate of candidates) {
    if (isFiniteNumber(candidate)) return candidate;
  }
  return null;
}

function firstString(candidates: any[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return null;
}

/**
 * Render a server timestamp, or an em dash. Never "Invalid Date" — a field this
 * page has not seen before is far likelier to be an unparseable string than a
 * date, and printing the JS engine's error text into an admin table is how a
 * cosmetic surprise gets mistaken for a data problem.
 */
function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

// ============================================
// PROJECTED COST DELTA  (SOW WS2)
// ============================================

/**
 * One call's contribution. `delta === null` means the server could not price
 * it — that call is EXCLUDED from the total rather than counted as zero, and
 * is named as excluded on screen. A missing measurement rendered as "no
 * change" is the same class of lie the readiness card exists to prevent.
 */
interface CostDeltaRow {
  call_name: string;
  from_model: string | null;
  to_model: string | null;
  from_cost: number | null;
  to_cost: number | null;
  delta: number | null;
}

interface CostDelta {
  /** null when the server states no total and no row was priceable. */
  total: number | null;
  currency: string;
  rows: CostDeltaRow[];
  /** Call names the server could not price. May be shorter than `excludedCount`. */
  excluded: string[];
  excludedCount: number;
  /** What the number is per — e.g. "per user per month". Server-stated only. */
  basis: string | null;
  detail: string | null;
}

function normalizeCostRow(raw: any): CostDeltaRow | null {
  if (!raw || typeof raw !== 'object') return null;

  const callName = firstString([
    raw.call_name, raw.call, raw.name, raw.llm_call, raw.call_id
  ]);
  if (!callName) return null;

  const fromCost = firstNumber([
    raw.from_monthly_usd, raw.from_cost_usd, raw.from_cost,
    raw.old_monthly_usd, raw.old_cost, raw.before, raw.from_usd
  ]);
  const toCost = firstNumber([
    raw.to_monthly_usd, raw.to_cost_usd, raw.to_cost,
    raw.new_monthly_usd, raw.new_cost, raw.after, raw.to_usd
  ]);

  let delta = firstNumber([
    raw.delta_monthly_usd, raw.monthly_delta_usd, raw.delta_usd,
    raw.delta, raw.change_usd, raw.change
  ]);
  if (delta === null && fromCost !== null && toCost !== null) {
    delta = toCost - fromCost;
  }

  // An explicit "could not price this" beats a derived number: the server knows
  // whether its inputs were real, and this browser does not.
  const estimable =
    raw.estimated === false || raw.estimable === false ||
    raw.inestimable === true || raw.unestimated === true ||
    raw.priced === false
      ? false
      : true;

  return {
    call_name: callName,
    from_model: firstString([raw.from_model, raw.old_model, raw.from, raw.model_from]),
    to_model: firstString([raw.to_model, raw.new_model, raw.to, raw.model_to]),
    from_cost: fromCost,
    to_cost: toCost,
    delta: estimable ? delta : null
  };
}

/**
 * Read the projected cost delta out of whatever the server sent.
 *
 * Alias-tolerant on purpose: this block is newer than some deployed builds of
 * this page, and the shape is still settling. Returns null when nothing usable
 * is present, which renders as "not available" — distinct from "$0.00", which
 * is a measurement.
 */
function normalizeCostDelta(report: ReadinessReport | null | undefined): CostDelta | null {
  if (!report || typeof report !== 'object') return null;

  // A server that states only a bare number is still stating a total. Handled
  // here so the panel renders it rather than reporting "not available" over a
  // figure it was actually given.
  const scalar = firstNumber([report.cost_delta, report.projected_cost_delta]);
  if (scalar !== null) {
    return {
      total: scalar, currency: 'USD', rows: [], excluded: [],
      excludedCount: 0, basis: null, detail: null
    };
  }

  const raw =
    (report.cost_delta && typeof report.cost_delta === 'object' && report.cost_delta) ||
    (report.projected_cost_delta && typeof report.projected_cost_delta === 'object' &&
      report.projected_cost_delta) ||
    ((report as any).cost && typeof (report as any).cost === 'object' && (report as any).cost);

  if (!raw || Array.isArray(raw)) return null;

  const rawRows =
    (Array.isArray(raw.per_call) && raw.per_call) ||
    (Array.isArray(raw.calls) && raw.calls) ||
    (Array.isArray(raw.rows) && raw.rows) ||
    (Array.isArray(raw.changes) && raw.changes) ||
    (Array.isArray(raw.items) && raw.items) ||
    [];

  const rows: CostDeltaRow[] = (rawRows as any[])
    .map(normalizeCostRow)
    .filter((row: CostDeltaRow | null): row is CostDeltaRow => row !== null);

  const priced: CostDeltaRow[] = rows.filter(row => row.delta !== null);

  const statedTotal = firstNumber([
    raw.total_monthly_delta_usd, raw.monthly_delta_usd, raw.total_delta_usd,
    raw.total_usd, raw.delta_monthly_usd, raw.total_delta, raw.delta_usd,
    raw.total, raw.delta
  ]);

  // A server-stated total always wins over arithmetic done in the browser: the
  // server may price things this page never sees a row for.
  const total = statedTotal !== null
    ? statedTotal
    : priced.length > 0
      ? priced.reduce((sum: number, row: CostDeltaRow) => sum + (row.delta as number), 0)
      : null;

  const rawExcluded =
    (Array.isArray(raw.inestimable_calls) && raw.inestimable_calls) ||
    (Array.isArray(raw.unestimated_calls) && raw.unestimated_calls) ||
    (Array.isArray(raw.excluded_calls) && raw.excluded_calls) ||
    (Array.isArray(raw.not_estimated) && raw.not_estimated) ||
    (Array.isArray(raw.unpriced_calls) && raw.unpriced_calls) ||
    [];

  const excludedNames = new Set<string>();
  for (const entry of rawExcluded) {
    const name = typeof entry === 'string'
      ? entry
      : firstString([entry?.call_name, entry?.call, entry?.name]);
    if (name) excludedNames.add(name);
  }
  for (const row of rows) {
    if (row.delta === null) excludedNames.add(row.call_name);
  }

  const statedExcludedCount = firstNumber([
    raw.inestimable_count, raw.unestimated_count, raw.excluded_count,
    raw.unpriced_count, raw.not_estimated_count
  ]);
  const excluded = Array.from(excludedNames);
  const excludedCount = Math.max(statedExcludedCount ?? 0, excluded.length);

  if (total === null && rows.length === 0 && excludedCount === 0) return null;

  return {
    total,
    currency: firstString([raw.currency, raw.currency_code]) || 'USD',
    rows,
    excluded,
    excludedCount,
    basis: firstString([raw.basis, raw.unit, raw.per, raw.scope]),
    detail: firstString([raw.detail, raw.note, raw.notes])
  };
}

/** `$12.34` — magnitude only, no sign. Never returns NaN. */
function formatMoney(value: number, currency: string): string {
  const magnitude = Math.abs(value);
  const digits = magnitude > 0 && magnitude < 0.01 ? 4 : 2;
  const symbol = currency === 'USD' ? '$' : '';
  const suffix = symbol ? '' : ` ${currency}`;
  return `${symbol}${magnitude.toFixed(digits)}${suffix}`;
}

/** `+$12.34` / `-$8.10` / `$0.00`. The sign is the point of this number. */
function formatMoneyDelta(value: number | null | undefined, currency: string): string {
  if (!isFiniteNumber(value)) return 'not available';
  const money = formatMoney(value, currency);
  if (value > 0) return `+${money}`;
  if (value < 0) return `-${money}`;
  return money;
}

function costTone(total: number | null): 'up' | 'down' | 'flat' | 'unknown' {
  if (!isFiniteNumber(total)) return 'unknown';
  if (total > 0) return 'up';
  if (total < 0) return 'down';
  return 'flat';
}

// ============================================
// PROJECTED COST DELTA — MEASURED-FIRST SHAPE
// (COST_DELTA_ACCURACY_SCOPE WS3)
// ============================================

/**
 * Which rung of the measured-first ladder priced a row. The order IS the trust
 * order: real ledger dollars beat measured tokens × price, which beat registry
 * estimates. `excluded` is the honest bottom — named, counted, never $0.
 */
type CostProvenance = 'measured_both' | 'measured_tokens' | 'modeled' | 'excluded';

/** Internal tier keys in display order, with their public names. */
const COST_TIER_ORDER = ['trial', 'basic', 'full', 'extended'];
const COST_TIER_LABELS: Record<string, string> = {
  trial: 'Trial',
  basic: 'Essentials',
  full: 'Chat+',
  extended: 'Pro'
};

function tierLabel(tier: string): string {
  return COST_TIER_LABELS[tier] || tier.replace(/_/g, ' ');
}

function tierSortIndex(tier: string): number {
  const index = COST_TIER_ORDER.indexOf(tier);
  return index === -1 ? COST_TIER_ORDER.length : index;
}

interface TierCostTotal {
  tier: string;
  /** null = floor-quality: the server declined to state a total for this tier. */
  delta: number | null;
  callsPriced: number | null;
  callsMissingFrequency: number | null;
}

interface CostDeltaV2Row {
  call_name: string;
  change_kind: string | null;
  from_model: string | null;
  to_model: string | null;
  provenance: CostProvenance;
  sample_n_from: number | null;
  sample_n_to: number | null;
  /** tier -> $/user/month delta; null = that tier could not be priced. */
  delta_by_tier: Record<string, number | null>;
  excluded_reason: string | null;
  excluded_detail: string | null;
}

interface CostDeltaV2 {
  available: boolean;
  detail: string | null;
  tierTotals: TierCostTotal[];
  /** % of measured 30-day FROM-side spend of the changed calls that got priced. */
  spendCoveragePct: number | null;
  coverageLabel: 'complete' | 'floor';
  rows: CostDeltaV2Row[];
  excludedByReason: Array<{ reason: string; count: number }>;
  /** One honest server sentence: windows, min-n, reasoning-token note. */
  basis: string | null;
  callsChanged: number;
  callsPriced: number;
}

/**
 * Read the tier-structured cost delta (per-tier $/user/month, provenance per
 * row, spend coverage). Handles the measured-first contract shape AND the v1
 * registry-modeled shape that preceded it — same skeleton (`available`,
 * `per_call` with per-tier dicts), v1 just states no provenance, which is
 * itself information: every v1 row was modeled from registry estimates.
 * Returns null for anything older, which falls back to `normalizeCostDelta`
 * and the legacy single-total panel.
 */
function normalizeCostDeltaV2(report: ReadinessReport | null | undefined): CostDeltaV2 | null {
  if (!report || typeof report !== 'object') return null;

  const raw: any =
    (report.cost_delta && typeof report.cost_delta === 'object' &&
      !Array.isArray(report.cost_delta) && report.cost_delta) ||
    (report.projected_cost_delta && typeof report.projected_cost_delta === 'object' &&
      !Array.isArray(report.projected_cost_delta) && report.projected_cost_delta);
  if (!raw) return null;

  // The fail-open answer. The panel never gates promotion, so "could not
  // compute" arrives as a shape of its own rather than a missing field —
  // render it with the server's reason instead of dropping into the legacy
  // alias hunt, which would misread it as "nothing was sent".
  if (raw.available === false) {
    return {
      available: false,
      detail: firstString([raw.detail, raw.note]),
      tierTotals: [], spendCoveragePct: null, coverageLabel: 'floor',
      rows: [], excludedByReason: [], basis: null,
      callsChanged: 0, callsPriced: 0
    };
  }

  // --- per-call rows ---
  const rawRows: any[] = Array.isArray(raw.per_call) ? raw.per_call : [];
  const rows: CostDeltaV2Row[] = [];
  for (const entry of rawRows) {
    if (!entry || typeof entry !== 'object') continue;
    const callName = firstString([entry.call_name, entry.call, entry.name]);
    if (!callName) continue;

    const deltaByTier: Record<string, number | null> = {};
    const deltaSource = entry.delta_monthly_usd_per_user;
    if (deltaSource && typeof deltaSource === 'object' && !Array.isArray(deltaSource)) {
      for (const [tier, value] of Object.entries(deltaSource)) {
        deltaByTier[tier] = isFiniteNumber(value) ? value : null;
      }
    }

    const excludedReason = firstString([entry.excluded_reason, entry.reason]);
    const stated = firstString([entry.provenance]);
    const provenance: CostProvenance =
      stated === 'measured_both' || stated === 'measured_tokens' ||
      stated === 'modeled' || stated === 'excluded'
        ? stated
        // v1 rows state no rung. Excluded is excluded; everything else was
        // priced from registry estimates by construction, so labeling it
        // "modeled" is accurate, not a guess.
        : excludedReason || entry.estimable === false ? 'excluded' : 'modeled';

    rows.push({
      call_name: callName,
      change_kind: firstString([entry.change_kind]),
      from_model: firstString([entry.from_model, entry.from]),
      to_model: firstString([entry.to_model, entry.to]),
      provenance,
      sample_n_from: firstNumber([entry.sample_n_from]),
      sample_n_to: firstNumber([entry.sample_n_to]),
      delta_by_tier: deltaByTier,
      excluded_reason: excludedReason,
      excluded_detail: firstString([entry.excluded_detail, entry.detail])
    });
  }

  // --- per-tier totals: the contract shape keys totals BY TIER
  // (totals.trial = {delta_monthly_usd_per_user, calls_priced, ...} | null);
  // v1 keyed them by metric (totals.delta_monthly_usd_per_user = {tier: n})
  // with coverage in a sibling tier_coverage map. ---
  const tierTotals: TierCostTotal[] = [];
  const rawTotals = raw.totals && typeof raw.totals === 'object' && !Array.isArray(raw.totals)
    ? raw.totals : null;
  if (rawTotals) {
    const metricKeyed = rawTotals.delta_monthly_usd_per_user &&
      typeof rawTotals.delta_monthly_usd_per_user === 'object';
    if (metricKeyed) {
      const coverage = raw.tier_coverage && typeof raw.tier_coverage === 'object'
        ? raw.tier_coverage : {};
      for (const [tier, value] of Object.entries(rawTotals.delta_monthly_usd_per_user)) {
        const cov = coverage[tier] && typeof coverage[tier] === 'object' ? coverage[tier] : {};
        tierTotals.push({
          tier,
          delta: isFiniteNumber(value) ? value : null,
          callsPriced: firstNumber([cov.calls_priced]),
          callsMissingFrequency: firstNumber([cov.calls_missing_frequency])
        });
      }
    } else {
      for (const [tier, value] of Object.entries(rawTotals)) {
        if (value === null) {
          // Floor-quality tier: the server refused to state a number rather
          // than state one missing its most uncertain parts.
          tierTotals.push({ tier, delta: null, callsPriced: null, callsMissingFrequency: null });
          continue;
        }
        if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
        tierTotals.push({
          tier,
          delta: firstNumber([(value as any).delta_monthly_usd_per_user]),
          callsPriced: firstNumber([(value as any).calls_priced]),
          callsMissingFrequency: firstNumber([(value as any).calls_missing_frequency])
        });
      }
    }
  }
  tierTotals.sort((a, b) =>
    tierSortIndex(a.tier) - tierSortIndex(b.tier) || a.tier.localeCompare(b.tier));

  // Not the tier-structured family at all (no `available`, no tier data
  // anywhere) — let the legacy normalizer and panel have it.
  const tierStructured =
    typeof raw.available === 'boolean' ||
    tierTotals.length > 0 ||
    rows.some(row => Object.keys(row.delta_by_tier).length > 0);
  if (!tierStructured) return null;

  // --- excluded histogram: server-stated, else grouped from the rows ---
  const excludedByReason: Array<{ reason: string; count: number }> = [];
  const ebr = raw.excluded_by_reason;
  if (ebr && typeof ebr === 'object' && !Array.isArray(ebr)) {
    for (const [reason, count] of Object.entries(ebr)) {
      if (isFiniteNumber(count) && count > 0) excludedByReason.push({ reason, count });
    }
  } else {
    const grouped = new Map<string, number>();
    for (const row of rows) {
      if (row.provenance !== 'excluded') continue;
      const reason = row.excluded_reason || 'unspecified';
      grouped.set(reason, (grouped.get(reason) || 0) + 1);
    }
    for (const [reason, count] of grouped) excludedByReason.push({ reason, count });
  }
  excludedByReason.sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));

  const counts = raw.counts && typeof raw.counts === 'object' ? raw.counts : {};
  const pricedRows = rows.filter(row => row.provenance !== 'excluded').length;
  const callsPriced = firstNumber([counts.calls_priced, counts.calls_estimated]) ?? pricedRows;
  const callsChanged = firstNumber([counts.calls_changed]) ??
    (rows.length > 0 ? rows.length : callsPriced);

  const spendCoveragePct = firstNumber([raw.spend_coverage_pct]);
  const statedLabel = firstString([raw.coverage_label]);
  const coverageLabel: 'complete' | 'floor' =
    statedLabel === 'complete' || statedLabel === 'floor'
      ? statedLabel
      : spendCoveragePct !== null
        ? spendCoveragePct >= 95 ? 'complete' : 'floor'
        // Neither a stated label nor a measured spend coverage — a v1-shape
        // response (or a deploy-skew window where the server predates the
        // measured-first contract). "complete" claims ">=95% of measured
        // 30-day spend" in its own tooltip, and this response measured no
        // spend at all, so counting rows can only ever fabricate that claim.
        // Floor is the honest default: priced-everything-we-tried is still
        // only a floor when nothing said how much of the money that covers.
        : 'floor';

  const basis = typeof raw.basis === 'string' && raw.basis.trim()
    ? raw.basis.trim()
    // v1 stated its basis as an object; `method` was its honest sentence.
    : firstString([raw.basis?.method, raw.basis?.source]);

  return {
    available: true,
    detail: firstString([raw.detail, raw.note]),
    tierTotals,
    spendCoveragePct,
    coverageLabel,
    rows,
    excludedByReason,
    basis,
    callsChanged,
    callsPriced
  };
}

/** `97.3%` — one decimal, trimmed to an integer when the decimal is .0. */
function formatPercent(value: number): string {
  const rounded = value.toFixed(1);
  return `${rounded.endsWith('.0') ? rounded.slice(0, -2) : rounded}%`;
}

const PROVENANCE_STYLE: Record<CostProvenance, { bg: string; color: string }> = {
  measured_both: { bg: '#E8F2ED', color: '#113C2B' },
  measured_tokens: { bg: '#E0F2FE', color: '#075985' },
  modeled: { bg: '#FEF3C7', color: '#92400E' },
  excluded: { bg: '#F3F4F6', color: '#4B5563' }
};

const PROVENANCE_TITLES: Record<CostProvenance, string> = {
  measured_both:
    'Both models priced from real 30-day ledger spend (llm_usage_actual) — the strongest ' +
    'rung. The dollars come from the ledger rows themselves, each priced at the model ' +
    'prices in force when it was written, so a price change inside the window blends both ' +
    'epochs. Ledger output tokens include reasoning tokens, so this does not undercount ' +
    'reasoning models the way registry estimates do.',
  measured_tokens:
    "From-model measured tokens × the to-model's current per-MTok price. Measured usage, " +
    'modeled price — the middle rung. The token profile is measured on the OUTGOING model, ' +
    'so it may understate a reasoning-heavier replacement.',
  modeled:
    'Registry token estimates × model_definitions prices — the weakest rung, labeled as such.',
  excluded:
    'Could not be priced. Not counted as zero — the totals are a floor without it.'
};

function provenanceChipText(row: CostDeltaV2Row): string {
  const fmt = (value: number | null) => (value === null ? null : value.toLocaleString());
  switch (row.provenance) {
    case 'measured_both': {
      const from = fmt(row.sample_n_from);
      const to = fmt(row.sample_n_to);
      if (from && to) return `measured (n=${from} → ${to})`;
      if (from || to) return `measured (n=${from || to})`;
      return 'measured';
    }
    case 'measured_tokens': {
      const from = fmt(row.sample_n_from);
      return from ? `measured tokens (n=${from}), priced` : 'measured tokens, priced';
    }
    case 'modeled':
      return 'modeled';
    case 'excluded':
      return 'excluded';
  }
}

function ProvenanceChip({ row }: { row: CostDeltaV2Row }) {
  const style = PROVENANCE_STYLE[row.provenance];
  return (
    <span
      className="inline-flex px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap"
      style={{ backgroundColor: style.bg, color: style.color }}
      title={
        row.provenance === 'excluded'
          ? row.excluded_detail || row.excluded_reason || PROVENANCE_TITLES.excluded
          : PROVENANCE_TITLES[row.provenance]
      }
    >
      {provenanceChipText(row)}
    </span>
  );
}

// ============================================
// REVERT DRIFT
// ============================================

/**
 * Read drift out of whatever the server hands back — a ledger row carrying it
 * inline, a preview payload, or a payload that nests it under `drift`.
 *
 * Returns null when nothing usable is present, which the dialog renders as
 * "not available" rather than as "no drift". Those are different claims and
 * conflating them would be the same lie this whole screen exists to avoid.
 */
function normalizeRevertDrift(raw: any): RevertDrift | null {
  if (!raw || typeof raw !== 'object') return null;

  const source =
    (raw.revert_drift && typeof raw.revert_drift === 'object' && raw.revert_drift) ||
    (raw.drift && typeof raw.drift === 'object' && raw.drift) ||
    (raw.revert_preview && typeof raw.revert_preview === 'object' && raw.revert_preview) ||
    raw;

  if (!source || typeof source !== 'object') return null;

  const counts = source.counts && typeof source.counts === 'object' ? source.counts : {};
  const modelPlusParam =
    isFiniteNumber(counts.model_changes) && isFiniteNumber(counts.param_changes)
      ? counts.model_changes + counts.param_changes
      : null;

  // WHICH calls differ, not just how many. A bare count tells an admin that the
  // restore target moved but not whether it moved somewhere they care about.
  const changedCalls = driftChangedCallNames(source);

  const callsChanged = firstNumber([
    source.calls_changed,
    source.calls_differ,
    // Only when it is a count; `changed_calls` is a list in the newer shape and
    // its length is used further down instead.
    source.changed_calls,
    source.drift_count,
    counts.calls_changed,
    counts.calls_differ,
    modelPlusParam,
    changedCalls.length > 0 ? changedCalls.length : null
  ]);

  const rawIssues = source.issues || source.readiness_issues || source.blocking_issues;
  const issues: ReadinessIssue[] = Array.isArray(rawIssues)
    ? rawIssues.filter((issue: any) => issue && typeof issue === 'object')
    : [];

  const ready = typeof source.ready === 'boolean' ? source.ready : null;
  const detail = typeof source.detail === 'string' && source.detail ? source.detail : null;

  if (
    callsChanged === null && changedCalls.length === 0 &&
    issues.length === 0 && ready === null && !detail
  ) {
    return null;
  }
  return { calls_changed: callsChanged, changed_calls: changedCalls, issues, ready, detail };
}

/**
 * Pull the changed call NAMES out of a drift payload, whichever way it states
 * them: a flat list of names, a list of change objects, or a diff_summary of
 * the same shape the readiness report uses. De-duplicated and stable-ordered.
 */
function driftChangedCallNames(source: any): string[] {
  if (!source || typeof source !== 'object') return [];

  const names = new Set<string>();

  const pushFrom = (value: any) => {
    if (!Array.isArray(value)) return;
    for (const entry of value) {
      if (typeof entry === 'string') {
        if (entry.trim()) names.add(entry.trim());
        continue;
      }
      const name = firstString([entry?.call_name, entry?.call, entry?.name]);
      if (name) names.add(name);
    }
  };

  pushFrom(source.changed_calls);
  pushFrom(source.changed_call_names);
  pushFrom(source.call_names);
  pushFrom(source.calls_changed_list);
  pushFrom(source.calls);
  pushFrom(source.differences);

  const diff = source.diff_summary && typeof source.diff_summary === 'object'
    ? source.diff_summary
    : source;
  pushFrom(diff.model_changes);
  pushFrom(diff.param_changes);
  pushFrom(diff.calls_only_in_live);
  pushFrom(diff.calls_only_in_candidate);

  return Array.from(names);
}

export const LLMProfiles: React.FC = () => {
  const [profiles, setProfiles] = useState<LLMProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<string>('default');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  // Bumped after a promote/revert so the history table reloads alongside the
  // profiles list.
  const [historyNonce, setHistoryNonce] = useState(0);
  // Bumped whenever the candidate's configuration is edited on this page. The
  // readiness report — and the probe evidence inside it — describes a specific
  // configuration; the moment that configuration changes the report is a
  // statement about something that no longer exists.
  const [readinessNonce, setReadinessNonce] = useState(0);

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await adminFetch(`${LLM_PROFILES_API}/profiles`);

      if (!response.ok) {
        throw new Error('Failed to fetch profiles');
      }

      const data = await response.json();
      setProfiles(data.profiles || []);
    } catch (error) {
      console.error('Failed to fetch LLM profiles:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const selected = profiles.find(p => p.profile_name === selectedProfile);
  const liveProfile = profiles.find(p => p.is_default);

  const handleLifecycleChange = useCallback(() => {
    setHistoryNonce(n => n + 1);
    fetchProfiles();
  }, [fetchProfiles]);

  /**
   * The configurator below edits the same profile the readiness card gates.
   * Without this the card kept its pre-edit report — including `probes_run` —
   * so an admin could probe green, repoint a call at an unservable model, and
   * still find the Promote button enabled over evidence for the old config.
   * Bumping the nonce forces a DB-only re-check, which lands with
   * probes_run=false and correctly disables Promote until they are re-run.
   */
  const handleCandidateConfigChanged = useCallback(() => {
    setReadinessNonce(n => n + 1);
    fetchProfiles();
  }, [fetchProfiles]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: '#0B1614' }}>LLM Model Profiles</h1>
        <p className="text-gray-600 mt-1">
          Configure AI models for all 93+ LLM calls across Coach Cedar
        </p>
      </div>

      {/* Profile Management Section */}
      <div className="bg-white p-6" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Profiles</h2>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors"
            style={{ backgroundColor: '#1F6C4F' }}
          >
            <Plus className="h-4 w-4" />
            <span>Create New Profile</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading profiles...</p>
          </div>
        ) : (
          <>
            {/* Profiles Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>
                      Profile Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>
                      Users
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {profiles.map(profile => (
                    <tr key={profile.profile_name} className={selectedProfile === profile.profile_name ? 'bg-green-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Settings className="h-4 w-4 mr-2 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {profile.display_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {profile.profile_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-md truncate">
                          {profile.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Users className="h-4 w-4 mr-1 text-gray-400" />
                          {profile.user_count}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <LifecycleBadge profile={profile} />
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            profile.is_system
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {profile.is_system ? 'System' : 'Custom'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => setSelectedProfile(profile.profile_name)}
                          className={`px-3 py-1 rounded-md transition-colors ${
                            selectedProfile === profile.profile_name
                              ? 'text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          style={selectedProfile === profile.profile_name ? { backgroundColor: '#1F6C4F' } : undefined}
                        >
                          {selectedProfile === profile.profile_name ? 'Editing' : 'Edit'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Selected Profile Indicator */}
            <div className="mt-4 p-3 rounded-md" style={{ backgroundColor: '#E8F2ED' }}>
              <p className="text-sm" style={{ color: '#113C2B' }}>
                <strong>Currently Editing:</strong> {selected?.display_name || 'Default Profile'}
                {selected?.is_default && ' — this is the LIVE profile; every user resolves through it.'}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Readiness + Promote (candidates only — the live profile has nothing to
          be promoted to) */}
      {!loading && selected && !selected.is_default && (
        <ReadinessCard
          key={selected.profile_name}
          profile={selected}
          liveDisplayName={liveProfile?.display_name || liveProfile?.profile_name || null}
          nonce={readinessNonce}
          onPromoted={handleLifecycleChange}
        />
      )}

      {/* Test Cohort */}
      {!loading && selected && (
        <CohortPanel
          key={`cohort-${selected.profile_name}`}
          profile={selected}
          onChanged={fetchProfiles}
        />
      )}

      {/* LLM Profile Configurator Component */}
      <LLMProfileConfigurator
        profileName={selectedProfile}
        onProfileUpdated={handleCandidateConfigChanged}
      />

      {/* Promotion History */}
      <PromotionHistory nonce={historyNonce} onReverted={handleLifecycleChange} />

      {/* Cost Modeling Workspace */}
      <div className="mt-8">
        <CostModelingWorkspace />
      </div>

      {/* Create Profile Modal */}
      {showCreateModal && (
        <CreateProfileModal
          profiles={profiles}
          onClose={() => setShowCreateModal(false)}
          onProfileCreated={() => {
            setShowCreateModal(false);
            fetchProfiles();
          }}
        />
      )}
    </div>
  );
};

// ============================================
// READINESS CARD
// ============================================

interface ReadinessCardProps {
  profile: LLMProfile;
  liveDisplayName: string | null;
  /** Bumped by the page whenever the candidate's configuration changes. */
  nonce: number;
  onPromoted: () => void;
}

/**
 * The gate. get_model_config never raises, so a candidate that is missing a
 * call does not fail — it silently resolves through a hardcoded fallback, or
 * for a call with no explicit entry, a generic gpt-5.1 @ 1024 tokens that
 * truncates. There is no error to find after the flip, so everything that can
 * be known has to be shown here, before it.
 */
function ReadinessCard({ profile, liveDisplayName, nonce, onPromoted }: ReadinessCardProps) {
  const [report, setReport] = useState<ReadinessReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applyingFix, setApplyingFix] = useState<string>('');
  const [fixNote, setFixNote] = useState('');
  const [showPromote, setShowPromote] = useState(false);
  // The promote dialog is rendered from the report. A refetch that fails sets
  // `report` to null, and without this the dialog would unmount mid-flight and
  // take the server's refusal message with it — the one thing the admin needs
  // to read at that moment.
  const lastReport = useRef<ReadinessReport | null>(null);

  const profileName = profile.profile_name;

  /**
   * `probe` is opt-in rather than on by default: each probe is a real, billed
   * vendor round-trip per distinct model, and this loads on every profile
   * selection. The DB-only pass is free and instant, which is what you want
   * while iterating on fixes; probing is the deliberate pre-flight step.
   */
  const load = useCallback(async (probe: boolean) => {
    setLoading(true);
    setError('');
    try {
      const response = await adminFetch(
        `${LLM_PROFILES_API}/profiles/${encodeURIComponent(profileName)}/readiness?probe=${probe ? 'true' : 'false'}`
      );
      if (!response.ok) {
        const { message } = await readError(response);
        throw new Error(message);
      }
      setReport(await response.json());
    } catch (err: any) {
      console.error('Failed to fetch readiness:', err);
      setReport(null);
      setError(err.message || 'Failed to fetch readiness');
    } finally {
      setLoading(false);
    }
  }, [profileName]);

  useEffect(() => {
    if (report) lastReport.current = report;
  }, [report]);

  // `nonce` is in the deps on purpose: a config save on this page does not
  // change `profileName`, so without it the effect never re-runs and the card
  // keeps describing (and vouching for) a configuration that no longer exists.
  useEffect(() => {
    load(false);
  }, [load, nonce]);

  /**
   * Apply one readiness fix.
   *
   * `callName` scopes it to the single call the issue names; omitting it keeps
   * the original profile-wide behaviour. The two are offered separately because
   * they are genuinely different actions: "raise this one call to its floor" and
   * "raise every under-floor call on the profile" have very different blast
   * radii, and the button that does the second should say so.
   *
   * `maxTokens` is sent only when the issue stated a target. A server that does
   * not yet understand either field ignores them (FastAPI drops unknown body
   * keys unless the model forbids them) and performs the profile-wide fix — the
   * pre-existing behaviour, never a silent no-op.
   */
  const applyFix = async (fixAction: string, callName?: string | null, maxTokens?: number | null) => {
    const key = fixKeyOf(fixAction, callName);
    setApplyingFix(key);
    setFixNote('');
    setError('');
    try {
      const body: Record<string, any> = { fix_action: fixAction };
      if (callName) body.call_name = callName;
      if (isFiniteNumber(maxTokens)) body.max_tokens = maxTokens;

      const response = await adminFetch(
        `${LLM_PROFILES_API}/profiles/${encodeURIComponent(profileName)}/prepare`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        const { message } = await readError(response);
        throw new Error(message);
      }
      const result = await response.json();
      const scope = callName ? ` (scoped to ${callName})` : '';
      // A server that ignored `call_name` did the profile-wide fix instead of
      // the one that was asked for. That is a WIDER change than the button
      // promised, so it gets said out loud rather than reported as success.
      const scopeHonoured =
        !callName ||
        !!result.call_name || !!result.scoped_to || result.scope === 'call' ||
        result.scoped === true ||
        !(isFiniteNumber(result.rows_touched) && result.rows_touched > 1);
      setFixNote(
        `${FIX_LABELS[fixAction] || fixAction}${scope}: ${result.rows_touched} row(s) updated on ${profileName}.` +
        (scopeHonoured
          ? ''
          : ' This server does not appear to support per-call fixes — it updated every' +
            ' matching call on the profile, not just the one requested. Re-check the diff below.')
      );
      // Probes are dropped by the refetch on purpose — the configuration just
      // changed, so the previous probe set no longer describes it.
      await load(false);
    } catch (err: any) {
      console.error('Failed to apply fix:', err);
      setError(err.message || 'Failed to apply fix');
    } finally {
      setApplyingFix('');
    }
  };

  if (loading && !report) {
    return (
      <div className="bg-white p-6" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
        <p className="text-sm text-gray-500">Checking readiness for {profile.display_name}…</p>
      </div>
    );
  }

  const blocking = report ? report.issues.filter(i => i.severity === 'block') : [];
  const warnings = report ? report.issues.filter(i => i.severity === 'warn') : [];
  // Two conditions, not one. `ready` can be true on a report where probes were
  // never run, and no vendor call happens inside the promote transaction — so
  // this is where an unservable model gets caught before the flip. It is a
  // pre-flight, not the last line of defence: the server refuses (409) if the
  // evidence behind this button is missing or no longer describes the
  // candidate, which is why a refusal here re-checks rather than retries.
  // Minutes since the evidence promote() will read was recorded. null when the
  // server did not return a probe_run (probe=false, or a pre-1705 build).
  const probeAgeMinutes: number | null = (() => {
    const at = report?.probe_run?.probed_at;
    if (!at) return null;
    const ms = Date.now() - new Date(at).getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 60000) : null;
  })();
  // The server refuses on stale evidence. Showing it here turns a 409 the admin
  // cannot predict into a warning they can act on.
  const probeStale = probeAgeMinutes !== null && probeAgeMinutes >= PROBE_MAX_AGE_MINUTES;

  const canPromote =
    !!report && report.ready && report.probes_run && !probeStale && !loading;

  return (
    <div className="bg-white p-6" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Promotion readiness — {profile.display_name}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Can this configuration safely replace{' '}
            <strong>{liveDisplayName || 'the live profile'}</strong> for every user?
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => load(false)}
            disabled={loading || !!applyingFix}
            className="flex items-center space-x-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Re-check</span>
          </button>
          <button
            onClick={() => load(true)}
            disabled={loading || !!applyingFix}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: '#113C2B' }}
            title="One live vendor call per distinct model, proving it is servable. Required before promoting."
          >
            <ShieldCheck className="h-4 w-4" />
            <span>Run vendor probes</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {fixNote && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-sm text-blue-800">{fixNote}</p>
        </div>
      )}

      {report && (
        <>
          {/* Summary counters */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <SummaryTile
              label="Blocking"
              value={report.summary.blocking_count}
              tone={report.summary.blocking_count > 0 ? 'bad' : 'good'}
            />
            <SummaryTile
              label="Warnings"
              value={report.summary.warning_count}
              tone={report.summary.warning_count > 0 ? 'warn' : 'good'}
            />
            <SummaryTile label="Live active calls" value={report.summary.live_active_calls} tone="neutral" />
            <SummaryTile label="Candidate active calls" value={report.summary.candidate_active_calls} tone="neutral" />
            <SummaryTile
              label="Users affected"
              value={impactTotal(report.impact)}
              tone="neutral"
            />
          </div>

          {/* Ready / not ready banner */}
          {report.ready ? (
            <div
              className="rounded-md p-4 mb-5 flex items-start"
              style={{ backgroundColor: '#E8F2ED', border: '1px solid #1F6C4F' }}
            >
              <CheckCircle className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" style={{ color: '#1F6C4F' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: '#113C2B' }}>
                  No blocking issues.
                </p>
                <p className="text-sm mt-1" style={{ color: '#113C2B' }}>
                  {report.probes_run
                    ? `${report.summary.models_probed} model(s) probed, ${report.summary.probe_failures} failure(s). This profile can be promoted.`
                    : 'Vendor probes have not been run yet. Promotion stays disabled until they are — no vendor call happens inside the promote transaction, so this is where an unservable model gets caught.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-5 flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-800">
                  {report.summary.blocking_count} blocking issue(s) — promotion is refused.
                </p>
                <p className="text-sm text-red-800 mt-1">
                  Each of these resolves silently at runtime rather than raising, so it
                  would degrade quality without producing an error to find.
                </p>
              </div>
            </div>
          )}

          {/* Issues — blocks first */}
          {report.issues.length > 0 && (
            <div className="space-y-4 mb-5">
              {blocking.length > 0 && (
                <IssueGroup
                  title={`Blocking (${blocking.length})`}
                  issues={blocking}
                  tone="block"
                  applyingFix={applyingFix}
                  onFix={applyFix}
                />
              )}
              {warnings.length > 0 && (
                <IssueGroup
                  title={`Warnings (${warnings.length})`}
                  issues={warnings}
                  tone="warn"
                  applyingFix={applyingFix}
                  onFix={applyFix}
                />
              )}
            </div>
          )}

          {/* Probe results */}
          <div className="mb-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Model probes</h3>
            {/* What promote() will actually read inside its lock. `probes_run`
                only means this page asked; this is the persisted evidence, and
                the server refuses without a fresh, matching one. */}
            {report.probe_run && (
              <div
                className={`mb-2 px-3 py-2 rounded-md text-sm ${
                  probeStale ? 'bg-amber-50 text-amber-900' : 'text-gray-600'
                }`}
                style={probeStale ? undefined : { backgroundColor: '#F3F3F3' }}
              >
                {probeStale ? (
                  <>
                    <strong>Probe evidence is stale</strong> — recorded{' '}
                    {probeAgeMinutes} minutes ago, past the {PROBE_MAX_AGE_MINUTES}-minute
                    limit. The server will refuse this promotion. Re-run the probes: a
                    model revoked since then would not have been caught.
                  </>
                ) : (
                  <>
                    Evidence recorded {probeAgeMinutes === 0 ? 'just now' : `${probeAgeMinutes} min ago`}
                    {report.probe_run.probed_by ? ` by ${report.probe_run.probed_by}` : ''}. Promotion
                    re-checks it against the candidate's current model set before flipping.
                  </>
                )}
              </div>
            )}
            {!report.probes_run ? (
              <p className="text-sm text-gray-500">
                Not run. Use <strong>Run vendor probes</strong> above — one live call per
                distinct model, proving the vendor will actually serve it.
              </p>
            ) : Object.keys(report.probe_results).length === 0 ? (
              <p className="text-sm text-gray-500">No active models to probe on this profile.</p>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="bg-white divide-y divide-gray-100">
                    {Object.entries(report.probe_results).map(([modelName, result]) => (
                      <tr key={modelName}>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                          {modelName}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            !result.ok
                              ? 'bg-red-100 text-red-800'
                              : result.skipped
                                ? 'bg-gray-100 text-gray-700'
                                : 'bg-green-100 text-green-800'
                          }`}>
                            {!result.ok ? 'FAILED' : result.skipped ? 'Not probed' : 'Servable'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">{result.detail || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* What changes + who it reaches */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="border border-gray-200 rounded-md p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">What changes</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>{report.diff_summary.counts.model_changes} call(s) change model</li>
                <li>{report.diff_summary.counts.param_changes} parameter change(s)</li>
                <li>{report.diff_summary.calls_only_in_live.length} call(s) only in live</li>
                <li>{report.diff_summary.calls_only_in_candidate.length} call(s) only in the candidate</li>
              </ul>
            </div>
            <div className="border border-gray-200 rounded-md p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Who it reaches</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                {impactComponents(report.impact).map(part => (
                  <li key={part.key}>{part.value} user(s) {part.label}</li>
                ))}
                <li className="font-semibold">{impactTotal(report.impact)} total affected</li>
              </ul>
              {impactStayingPut(report.impact) > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  The total counts {impactStayingPut(report.impact)} admin-pinned user(s) who
                  will stay on the outgoing profile — it over-reports on purpose.
                </p>
              )}
            </div>
          </div>

          {/* What it costs. The dialog derives its own view from the report it
              is handed (which may be `lastReport` after a failed refetch), so
              the two always describe the same report rather than two
              different snapshots. */}
          <CostDeltaPanel report={report} />

          {/* Promote */}
          <div className="flex items-center justify-between border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-600">
              {canPromote
                ? 'Ready. Promotion flips the pointer — the outgoing profile is left untouched as the revert target.'
                : report.ready && probeStale
                  ? `Probe evidence is ${probeAgeMinutes} minutes old — re-run the probes to enable promotion.`
                  : report.ready && !report.probes_run
                    ? 'Run vendor probes to enable promotion.'
                    : 'Resolve the blocking issues above to enable promotion.'}
            </p>
            <button
              onClick={() => setShowPromote(true)}
              disabled={!canPromote}
              className="flex items-center space-x-2 px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              style={canPromote ? { backgroundColor: '#1F6C4F' } : undefined}
            >
              <Rocket className="h-4 w-4" />
              <span>Make this the live profile</span>
            </button>
          </div>
        </>
      )}

      {showPromote && (report || lastReport.current) && (
        <PromoteDialog
          profile={profile}
          report={(report || lastReport.current) as ReadinessReport}
          liveDisplayName={liveDisplayName}
          // A 409 means the server refused on state the browser no longer has
          // right. Pull the truth back down so the card behind the dialog stops
          // showing the state that was just rejected.
          onReadinessStale={() => { void load(false); }}
          onClose={() => setShowPromote(false)}
          onPromoted={() => {
            setShowPromote(false);
            onPromoted();
          }}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, tone }: { label: string; value: number; tone: 'good' | 'warn' | 'bad' | 'neutral' }) {
  const palette = {
    good: { bg: '#E8F2ED', color: '#113C2B' },
    warn: { bg: '#FEF3C7', color: '#92400E' },
    bad: { bg: '#FEE2E2', color: '#991B1B' },
    neutral: { bg: '#F3F4F6', color: '#374151' }
  }[tone];
  return (
    <div className="rounded-md p-3" style={{ backgroundColor: palette.bg }}>
      <div className="text-xl font-bold" style={{ color: palette.color }}>{value}</div>
      <div className="text-xs mt-0.5" style={{ color: palette.color }}>{label}</div>
    </div>
  );
}

/**
 * Dispatcher. The measured-first tier-structured shape (and its v1
 * predecessor) gets the new panel; anything older falls back to the legacy
 * single-total renderer; nothing usable falls all the way through to the
 * honest "not available". Old servers therefore render exactly as before.
 */
function CostDeltaPanel({ report }: { report: ReadinessReport }) {
  const v2 = normalizeCostDeltaV2(report);
  if (v2) return <CostDeltaPanelV2 cost={v2} />;
  return <CostDeltaPanelLegacy cost={normalizeCostDelta(report)} />;
}

/**
 * Measured-first cost panel (COST_DELTA_ACCURACY_SCOPE WS3).
 *
 * Three claims have to be legible at a glance: what each tier's change is per
 * user per month, how much of the real (measured) spend those totals cover,
 * and exactly why anything was left out. Coverage is stated by SPEND, not by
 * count — 9 unpriced micro-classifiers are not 9 unpriced planner calls, and
 * count-based coverage lies. Every row carries its ladder rung, so no number
 * appears without a source.
 */
function CostDeltaPanelV2({ cost }: { cost: CostDeltaV2 }) {
  const [expanded, setExpanded] = useState(false);

  if (!cost.available) {
    return (
      <div className="border border-gray-200 rounded-md p-4 mb-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Projected cost delta</h3>
        <p className="text-sm text-gray-600">
          Not available{cost.detail ? <> — {cost.detail}</> : ''}. Absence of an estimate is
          not an estimate of zero — promotion is not gated on it, but size the change from
          the model diff above rather than assuming cost is unchanged.
        </p>
      </div>
    );
  }

  const excludedCount = Math.max(
    cost.callsChanged - cost.callsPriced,
    cost.excludedByReason.reduce((sum, entry) => sum + entry.count, 0)
  );
  const isFloor = cost.coverageLabel === 'floor';

  // Which tiers get columns in the breakdown: whatever the totals state,
  // else whatever the rows carry, else the canonical four.
  const tierColumns = (() => {
    const tiers = new Set<string>();
    cost.tierTotals.forEach(total => tiers.add(total.tier));
    if (tiers.size === 0) {
      cost.rows.forEach(row => Object.keys(row.delta_by_tier).forEach(t => tiers.add(t)));
    }
    if (tiers.size === 0) COST_TIER_ORDER.forEach(t => tiers.add(t));
    return Array.from(tiers).sort((a, b) =>
      tierSortIndex(a) - tierSortIndex(b) || a.localeCompare(b));
  })();

  const tileStyle = (delta: number | null) => ({
    up: { bg: '#FEF3C7', color: '#92400E' },
    down: { bg: '#E8F2ED', color: '#113C2B' },
    flat: { bg: '#F3F4F6', color: '#374151' },
    unknown: { bg: '#F3F4F6', color: '#4B5563' }
  }[costTone(delta)]);

  return (
    <div className="border border-gray-200 rounded-md p-4 mb-5">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Projected cost delta</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Per user per month, priced measured-first from the usage ledger.
          </p>
        </div>
        <span
          className="px-2 py-1 text-xs font-semibold rounded whitespace-nowrap"
          style={isFloor
            ? { backgroundColor: '#FEF3C7', color: '#92400E' }
            : { backgroundColor: '#E8F2ED', color: '#113C2B' }}
          title={isFloor
            ? 'Unpriced calls are missing from these totals, so the real change is at least this.'
            : 'Priced calls cover at least 95% of the measured 30-day spend of the changed calls.'}
        >
          {isFloor ? 'floor — real change is at least this' : '≈ complete'}
        </span>
      </div>

      {/* Coverage headline — by dollars, not by call count. */}
      <p className="text-sm text-gray-700 mb-3">
        Priced <strong>{cost.callsPriced}</strong> of{' '}
        <strong>{cost.callsChanged}</strong> changed call(s)
        {cost.spendCoveragePct !== null && (
          <> covering <strong>{formatPercent(cost.spendCoveragePct)}</strong> of measured
          30-day spend</>
        )}.
      </p>

      {/* Per-tier totals */}
      {cost.tierTotals.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          {cost.tierTotals.map(total => {
            const style = tileStyle(total.delta);
            return (
              <div key={total.tier} className="rounded-md p-3" style={{ backgroundColor: style.bg }}>
                <div className="text-xs font-semibold" style={{ color: style.color }}>
                  {tierLabel(total.tier)}
                </div>
                <div className="text-lg font-bold leading-tight" style={{ color: style.color }}>
                  {total.delta === null ? 'not priced' : formatMoneyDelta(total.delta, 'USD')}
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: style.color }}>
                  {total.delta === null ? 'no total stated for this tier' : '/user/mo'}
                  {total.callsPriced !== null ? ` · ${total.callsPriced} priced` : ''}
                  {total.callsMissingFrequency
                    ? ` · ${total.callsMissingFrequency} no freq`
                    : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* The excluded box, grouped by reason — the diagnosis at one glance. */}
      {excludedCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3">
          <p className="text-sm text-amber-900">
            <strong>{excludedCount} call(s) could not be priced and are excluded.</strong>{' '}
            They are not counted as zero — their real cost is unknown, so the totals above
            are a floor on the change, not the whole of it.
          </p>
          {cost.excludedByReason.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {cost.excludedByReason.map(entry => (
                <span
                  key={entry.reason}
                  className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-900 whitespace-nowrap"
                >
                  {entry.reason.replace(/_/g, ' ')} × {entry.count}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {cost.detail && <p className="text-sm text-gray-600 mb-3">{cost.detail}</p>}

      {cost.rows.length > 0 ? (
        <>
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-sm font-medium hover:underline"
            style={{ color: '#1F6C4F' }}
          >
            {expanded
              ? 'Hide per-call breakdown'
              : `Show per-call breakdown (${cost.rows.length} call${cost.rows.length === 1 ? '' : 's'})`}
          </button>

          {expanded && (
            <div className="overflow-x-auto border border-gray-200 rounded-md mt-3">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    {['Call', 'Model', 'Source',
                      ...tierColumns.map(tier => `${tierLabel(tier)} Δ/user/mo`)
                    ].map(heading => (
                      <th
                        key={heading}
                        className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                        style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {cost.rows.map((row, index) => (
                    <tr key={`${row.call_name}-${index}`}>
                      <td className="px-4 py-2 text-sm font-mono text-gray-900 whitespace-nowrap">
                        {row.call_name}
                        {row.provenance === 'excluded' &&
                          (row.excluded_reason || row.excluded_detail) && (
                          <div className="text-xs font-sans text-amber-800 whitespace-normal max-w-md">
                            {(row.excluded_reason || '').replace(/_/g, ' ')}
                            {row.excluded_detail ? ` — ${row.excluded_detail}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                        {row.from_model || row.to_model
                          ? `${row.from_model || '—'} → ${row.to_model || '—'}`
                          : '—'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <ProvenanceChip row={row} />
                      </td>
                      {tierColumns.map(tier => {
                        const delta = row.delta_by_tier[tier];
                        return (
                          <td key={tier} className="px-4 py-2 text-sm whitespace-nowrap">
                            {!isFiniteNumber(delta) ? (
                              <span
                                className="text-gray-400"
                                title={row.provenance === 'excluded'
                                  ? 'excluded — not priced'
                                  : 'not priced for this tier (no frequency)'}
                              >
                                —
                              </span>
                            ) : (
                              <span
                                className="font-medium"
                                style={{
                                  color: delta > 0 ? '#92400E' : delta < 0 ? '#113C2B' : '#374151'
                                }}
                              >
                                {formatMoneyDelta(delta, 'USD')}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-500">
          No per-call breakdown was returned — only the totals above.
        </p>
      )}

      {cost.basis && <p className="text-xs text-gray-500 mt-3">{cost.basis}</p>}
    </div>
  );
}

/**
 * Projected cost delta (SOW WS2) — legacy single-total shape.
 *
 * Three things have to be true on screen at once: the total is prominent and
 * signed, the per-call working is available without dominating the card, and
 * anything the server could NOT price is named as excluded. The third is the
 * one that matters — a total that quietly omits four calls reads as complete,
 * and an admin would size the promotion against a number that is missing its
 * most uncertain parts.
 */
function CostDeltaPanelLegacy({ cost }: { cost: CostDelta | null }) {
  const [expanded, setExpanded] = useState(false);

  if (!cost) {
    return (
      <div className="border border-gray-200 rounded-md p-4 mb-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Projected cost delta</h3>
        <p className="text-sm text-gray-600">
          Not available from this server. Absence of an estimate is not an estimate of
          zero — promotion is not gated on it, but size the change from the model diff
          above rather than assuming cost is unchanged.
        </p>
      </div>
    );
  }

  const tone = costTone(cost.total);
  const toneStyle = {
    up: { bg: '#FEF3C7', color: '#92400E' },
    down: { bg: '#E8F2ED', color: '#113C2B' },
    flat: { bg: '#F3F4F6', color: '#374151' },
    unknown: { bg: '#F3F4F6', color: '#4B5563' }
  }[tone];

  const partial = cost.excludedCount > 0;

  return (
    <div className="border border-gray-200 rounded-md p-4 mb-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Projected cost delta</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {cost.basis || 'Estimated from the model diff and measured per-call costs.'}
          </p>
        </div>
        <div className="rounded-md px-4 py-2 text-right" style={{ backgroundColor: toneStyle.bg }}>
          <div className="text-2xl font-bold leading-tight" style={{ color: toneStyle.color }}>
            {formatMoneyDelta(cost.total, cost.currency)}
          </div>
          <div className="text-xs" style={{ color: toneStyle.color }}>
            {tone === 'unknown'
              ? 'no total available'
              : tone === 'flat'
                ? 'no projected change'
                : tone === 'up' ? 'more per month' : 'less per month'}
            {partial ? ' · partial' : ''}
          </div>
        </div>
      </div>

      {partial && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3">
          <p className="text-sm text-amber-900">
            <strong>
              This total is incomplete: {cost.excludedCount} call(s) could not be estimated
              and are excluded from it.
            </strong>{' '}
            They are not counted as zero — their real cost is unknown, so the figure above
            is a floor on the change, not the whole of it.
          </p>
          {cost.excluded.length > 0 && (
            <p className="text-xs text-amber-900 mt-2 font-mono break-words">
              {cost.excluded.slice(0, 12).join(', ')}
              {cost.excluded.length > 12 ? ` … +${cost.excluded.length - 12} more` : ''}
            </p>
          )}
        </div>
      )}

      {cost.detail && <p className="text-sm text-gray-600 mb-3">{cost.detail}</p>}

      {cost.rows.length > 0 ? (
        <>
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-sm font-medium hover:underline"
            style={{ color: '#1F6C4F' }}
          >
            {expanded
              ? 'Hide per-call breakdown'
              : `Show per-call breakdown (${cost.rows.length} call${cost.rows.length === 1 ? '' : 's'})`}
          </button>

          {expanded && (
            <div className="overflow-x-auto border border-gray-200 rounded-md mt-3">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    {['Call', 'Model', 'Est. monthly change'].map(heading => (
                      <th
                        key={heading}
                        className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                        style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {cost.rows.map((row, index) => (
                    <tr key={`${row.call_name}-${index}`}>
                      <td className="px-4 py-2 text-sm font-mono text-gray-900 whitespace-nowrap">
                        {row.call_name}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                        {row.from_model || row.to_model
                          ? `${row.from_model || '—'} → ${row.to_model || '—'}`
                          : '—'}
                      </td>
                      <td className="px-4 py-2 text-sm whitespace-nowrap">
                        {row.delta === null ? (
                          <span className="text-amber-800">not estimated — excluded</span>
                        ) : (
                          <span
                            className="font-medium"
                            style={{ color: row.delta > 0 ? '#92400E' : row.delta < 0 ? '#113C2B' : '#374151' }}
                          >
                            {formatMoneyDelta(row.delta, cost.currency)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-500">
          No per-call breakdown was returned — only the total above.
        </p>
      )}
    </div>
  );
}

interface IssueGroupProps {
  title: string;
  issues: ReadinessIssue[];
  tone: 'block' | 'warn';
  /** Composite key from `fixKeyOf` — action, plus the call when scoped. */
  applyingFix: string;
  onFix: (fixAction: string, callName?: string | null, maxTokens?: number | null) => void;
}

function IssueGroup({ title, issues, tone, applyingFix, onFix }: IssueGroupProps) {
  const isBlock = tone === 'block';
  return (
    <div>
      <h3 className={`text-sm font-semibold mb-2 ${isBlock ? 'text-red-800' : 'text-amber-800'}`}>
        {title}
      </h3>
      <div className="space-y-2">
        {issues.map((issue, index) => (
          <div
            key={`${issue.kind}-${issue.call_name || issue.model_name || index}`}
            className={`rounded-md p-3 border ${
              isBlock ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
            }`}
          >
            <div className="flex items-start justify-between space-x-3">
              <div className="min-w-0">
                <div className="flex items-center flex-wrap gap-2 mb-1">
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                    isBlock ? 'bg-red-200 text-red-900' : 'bg-amber-200 text-amber-900'
                  }`}>
                    {issue.kind}
                  </span>
                  {issue.call_name && (
                    <span className="text-xs font-mono text-gray-700">{issue.call_name}</span>
                  )}
                  {issue.model_name && (
                    <span className="text-xs text-gray-500">{issue.model_name}</span>
                  )}
                </div>
                <p className={`text-sm ${isBlock ? 'text-red-800' : 'text-amber-900'}`}>
                  {issue.detail}
                </p>
              </div>
              {issue.fix_action && (
                <IssueFixButtons
                  issue={issue}
                  applyingFix={applyingFix}
                  onFix={onFix}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The fix control for one issue.
 *
 * When the issue names a single call, the scoped fix is offered FIRST and is
 * the primary action — it is what the issue is actually about. The profile-wide
 * variant stays available beside it, labelled as what it is, because one press
 * clearing eight identical issues is often exactly what an admin wants. When
 * the issue names no call (a profile-level problem) only the profile-wide
 * action makes sense and it is shown alone, as before.
 */
function IssueFixButtons({
  issue, applyingFix, onFix
}: {
  issue: ReadinessIssue;
  applyingFix: string;
  onFix: (fixAction: string, callName?: string | null, maxTokens?: number | null) => void;
}) {
  const action = issue.fix_action as string;
  const callName = issue.call_name;
  const target = recommendedMaxTokens(issue);
  const busy = !!applyingFix;

  const wideKey = fixKeyOf(action);
  const scopedKey = fixKeyOf(action, callName);
  const wideLabel = FIX_LABELS[action] || action;

  if (!callName) {
    return (
      <button
        onClick={() => onFix(action)}
        disabled={busy}
        className="flex-shrink-0 px-3 py-1.5 text-xs text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        style={{ backgroundColor: '#1F6C4F' }}
        title="Applies to the candidate profile only — the live profile is never edited, because it is the revert target. This fix is profile-wide and may clear several issues at once."
      >
        {applyingFix === wideKey ? 'Applying…' : wideLabel}
      </button>
    );
  }

  const scopedLabel = FIX_LABELS_SCOPED[action] || wideLabel;

  return (
    <div className="flex-shrink-0 flex flex-col items-end space-y-1">
      <button
        onClick={() => onFix(action, callName, target)}
        disabled={busy}
        className="px-3 py-1.5 text-xs text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
        style={{ backgroundColor: '#1F6C4F' }}
        title={
          `Applies to ${callName} on the candidate profile only` +
          (action === 'raise_max_tokens' && target
            ? `, raising max_tokens to ${target}.`
            : '.') +
          ' The live profile is never edited — it is the revert target.'
        }
      >
        {applyingFix === scopedKey
          ? 'Applying…'
          : `${scopedLabel}${action === 'raise_max_tokens' && target ? ` → ${target}` : ''}`}
      </button>
      <button
        onClick={() => onFix(action)}
        disabled={busy}
        className="px-2 py-1 text-[11px] text-gray-600 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
        title="Applies the same fix to every affected call on the candidate profile. May clear several issues at once."
      >
        {applyingFix === wideKey ? 'Applying…' : `${wideLabel} (all calls)`}
      </button>
    </div>
  );
}

// ============================================
// PROMOTE DIALOG
// ============================================

interface PromoteDialogProps {
  profile: LLMProfile;
  report: ReadinessReport;
  liveDisplayName: string | null;
  /** Called when the server refuses on state (409) so the card can re-check. */
  onReadinessStale: () => void;
  onClose: () => void;
  onPromoted: () => void;
}

function PromoteDialog({
  profile, report, liveDisplayName, onReadinessStale, onClose, onPromoted
}: PromoteDialogProps) {
  const [typed, setTyped] = useState('');
  const [promoting, setPromoting] = useState(false);
  const [error, setError] = useState('');
  const [blockingIssues, setBlockingIssues] = useState<ReadinessIssue[]>([]);
  const [result, setResult] = useState<PromotionEntry | null>(null);
  // Set when the server answered 409. Everything on this dialog was rendered
  // from a report the server has just contradicted, so retrying from here would
  // be a second click on the same rejected state.
  const [refused, setRefused] = useState(false);
  // Minted ONCE per dialog. Retrying after a failure must replay the same
  // promotion, not start a second one.
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const confirmed = typed.trim() === profile.profile_name;
  // A cost change is a consequence of this flip, so it belongs at the moment of
  // committing and not only on the card behind the dialog. Tier-structured
  // (measured-first) shape preferred; older shapes fall back to the legacy
  // single total; nothing usable renders the honest "unknown".
  const costV2 = normalizeCostDeltaV2(report);
  const cost = costV2 ? null : normalizeCostDelta(report);

  const handlePromote = async () => {
    if (!confirmed) return;
    setPromoting(true);
    setError('');
    setBlockingIssues([]);
    try {
      const response = await adminFetch(
        `${LLM_PROFILES_API}/profiles/${encodeURIComponent(profile.profile_name)}/promote`,
        {
          method: 'POST',
          body: JSON.stringify({
            idempotency_key: idempotencyKey,
            // What the admin actually typed, not the name from the URL. Sending
            // the path value back makes the server's confirmation check compare
            // a string to itself, which can never fail — the keystrokes have to
            // be the thing that travels.
            confirm_profile_name: typed.trim()
          })
        }
      );
      if (!response.ok) {
        const { message, issues } = await readError(response);
        setBlockingIssues(issues);
        // 409 is the state-conflict answer: readiness blocks, missing or stale
        // probe evidence, a candidate that moved underneath us. Whatever the
        // reason, this dialog's numbers are now known-wrong.
        if (response.status === 409) {
          setRefused(true);
          onReadinessStale();
        }
        throw new Error(message);
      }
      setResult(await response.json());
    } catch (err: any) {
      console.error('Failed to promote profile:', err);
      setError(err.message || 'Failed to promote profile');
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Rocket className="h-6 w-6 mr-2" style={{ color: '#1F6C4F' }} />
            <h2 className="text-xl font-semibold text-gray-900">
              {result ? 'Profile promoted' : 'Make this the live profile'}
            </h2>
          </div>
          <button
            onClick={result ? onPromoted : onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {result ? (
            <div className="rounded-md p-4" style={{ backgroundColor: '#E8F2ED' }}>
              <p className="text-sm font-semibold" style={{ color: '#113C2B' }}>
                {result.from_profile} → {result.to_profile}
                {result.replayed && ' (already applied — replayed)'}
              </p>
              <ul className="text-sm mt-2 space-y-1" style={{ color: '#113C2B' }}>
                <li>Ledger entry #{result.seq}</li>
                <li>{countOf(result.users_affected)} user(s) affected</li>
                <li>{result.pins_deleted} shadowing pin(s) deleted</li>
              </ul>
              <p className="text-sm mt-3" style={{ color: '#113C2B' }}>
                <strong>{result.from_profile}</strong> was not modified. Revert from the
                Promotion History table restores it exactly.
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm font-semibold text-red-800">
                    {refused ? 'The server refused this promotion.' : 'Promotion failed.'}
                  </p>
                  <p className="text-sm text-red-800 mt-1">{error}</p>
                  {blockingIssues.length > 0 && (
                    <ul className="text-sm text-red-800 mt-2 list-disc list-inside space-y-1">
                      {blockingIssues.map((issue, index) => (
                        <li key={`${issue.kind}-${index}`}>
                          {issue.call_name ? `${issue.call_name}: ` : ''}{issue.detail}
                        </li>
                      ))}
                    </ul>
                  )}
                  {refused && (
                    <p className="text-sm text-red-800 mt-3">
                      Nothing changed. Readiness has been re-checked behind this dialog —
                      close it, read the refreshed report, resolve the above and re-run the
                      vendor probes before trying again.
                    </p>
                  )}
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                <p className="text-sm text-amber-900">
                  <strong>This changes the LLM configuration for every user at once.</strong>{' '}
                  There is no canary. Revert is one action and stays available.
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">What changes</h3>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>{report.diff_summary.counts.model_changes} call(s) change model</li>
                  <li>{report.diff_summary.counts.param_changes} parameter change(s)</li>
                  <li>
                    {report.summary.live_active_calls} active call(s) live →{' '}
                    {report.summary.candidate_active_calls} on{' '}
                    <strong>{profile.display_name}</strong>
                  </li>
                  {report.summary.warning_count > 0 && (
                    <li className="text-amber-800">
                      {report.summary.warning_count} non-blocking warning(s) will ship as-is
                    </li>
                  )}
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">What it costs</h3>
                {costV2 ? (
                  costV2.available ? (
                    <>
                      {costV2.tierTotals.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {costV2.tierTotals.map(total => {
                            const tone = costTone(total.delta);
                            const style = {
                              up: { backgroundColor: '#FEF3C7', color: '#92400E' },
                              down: { backgroundColor: '#E8F2ED', color: '#113C2B' },
                              flat: { backgroundColor: '#F3F4F6', color: '#374151' },
                              unknown: { backgroundColor: '#F3F4F6', color: '#4B5563' }
                            }[tone];
                            return (
                              <span
                                key={total.tier}
                                className="px-2 py-1 text-xs font-semibold rounded whitespace-nowrap"
                                style={style}
                              >
                                {tierLabel(total.tier)}{' '}
                                {total.delta === null
                                  ? 'not priced'
                                  : `${formatMoneyDelta(total.delta, 'USD')}/user/mo`}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-sm text-gray-700">
                        Priced {costV2.callsPriced} of {costV2.callsChanged} changed call(s)
                        {costV2.spendCoveragePct !== null && (
                          <> covering{' '}
                          <strong>{formatPercent(costV2.spendCoveragePct)}</strong> of
                          measured 30-day spend</>
                        )}.
                      </p>
                      {costV2.coverageLabel === 'floor' && (
                        <p className="text-sm text-amber-800 mt-1">
                          These totals are a <strong>floor</strong>, not the whole change —
                          unpriced calls are missing from them, not counted as zero. The
                          real change is at least this.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-600">
                      No cost projection was available for this promotion
                      {costV2.detail ? <> — {costV2.detail}</> : ''}. That is not the same
                      as "no change" — the cost impact is simply unknown here.
                    </p>
                  )
                ) : cost ? (
                  <>
                    <p className="text-sm text-gray-700">
                      Projected change:{' '}
                      <strong
                        style={{
                          color: costTone(cost.total) === 'up'
                            ? '#92400E'
                            : costTone(cost.total) === 'down' ? '#113C2B' : '#374151'
                        }}
                      >
                        {formatMoneyDelta(cost.total, cost.currency)}
                      </strong>
                      {cost.basis ? ` ${cost.basis}` : ' per month'}
                      {cost.rows.length > 0
                        ? ` across ${cost.rows.length} changed call(s).`
                        : '.'}
                    </p>
                    {cost.excludedCount > 0 && (
                      <p className="text-sm text-amber-800 mt-1">
                        Incomplete: {cost.excludedCount} call(s) could not be estimated and are
                        excluded from that figure — treat it as a floor, not the whole change.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-600">
                    No cost estimate was returned for this promotion. That is not the same as
                    "no change" — the cost impact is simply unknown here.
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Who is affected</h3>
                <p className="text-sm text-gray-700">
                  <strong>{impactTotal(report.impact)}</strong> user(s) in total, made up of:
                </p>
                <ul className="text-sm text-gray-700 mt-1 space-y-1 list-disc list-inside">
                  {impactComponents(report.impact).map(part => (
                    <li key={part.key}>
                      <strong>{part.value}</strong> {part.label}
                    </li>
                  ))}
                </ul>
                {impactStayingPut(report.impact) > 0 && (
                  <p className="text-sm text-amber-800 mt-2">
                    {impactStayingPut(report.impact)} of those are counted but will not move —
                    an admin pinned them to the outgoing profile and the promotion does not
                    delete that kind of pin. The total over-reports on purpose.
                  </p>
                )}
                {impactPinsDeletedEstimate(report.impact) !== null && (
                  <p className="text-sm text-gray-600 mt-1">
                    About {impactPinsDeletedEstimate(report.impact)} pin(s) are deleted by this
                    promotion. Revert does not recreate them.
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Revert target</h3>
                <p className="text-sm text-gray-700">
                  <strong>{liveDisplayName || report.live_profile || 'none'}</strong> stays
                  untouched and becomes the revert target. Nothing is copied, so reverting
                  restores it exactly.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type <span className="font-mono font-semibold">{profile.profile_name}</span> to confirm
                </label>
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={profile.profile_name}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-600 focus:border-green-600"
                  disabled={promoting}
                  autoFocus
                />
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          {result ? (
            <button
              onClick={onPromoted}
              className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors"
              style={{ backgroundColor: '#1F6C4F' }}
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={promoting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePromote}
                // `refused`: a 409 was answered against the state this dialog is
                // built from. Re-clicking would only re-send it. The way back in
                // is through the refreshed readiness card.
                disabled={promoting || !confirmed || refused}
                className="flex items-center space-x-2 px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                style={promoting || !confirmed || refused ? undefined : { backgroundColor: '#1F6C4F' }}
                title={refused ? 'Readiness was refused — close and review the refreshed report.' : undefined}
              >
                {promoting ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>Promoting...</span>
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Promote</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// TEST COHORT
// ============================================

/**
 * Where the current membership might be served from. The roster endpoint is
 * newer than this file; a build without it answers 404 and the panel says so
 * rather than rendering an empty list, which would read as "nobody is pinned"
 * — the opposite of the truth for a cohort under test.
 */
const COHORT_MEMBER_PATHS = ['cohort', 'cohort-members', 'users'];

/** Pull the member array out of whichever envelope the server used. */
function normalizeCohortMembers(payload: any): CohortMember[] | null {
  if (!payload) return null;
  const list =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload.members) && payload.members) ||
    (Array.isArray(payload.users) && payload.users) ||
    (Array.isArray(payload.cohort) && payload.cohort) ||
    (Array.isArray(payload.assignments) && payload.assignments) ||
    null;
  if (!list) return null;

  return list
    .map((entry: any): CohortMember | null => {
      if (typeof entry === 'string') {
        return entry.trim()
          ? { email: entry.trim(), assigned_at: null, assigned_by: null }
          : null;
      }
      const email = firstString([entry?.email, entry?.user_email, entry?.address]);
      if (!email) return null;
      return {
        email,
        assigned_at: firstString([entry?.assigned_at, entry?.created_at, entry?.pinned_at]),
        assigned_by: firstString([entry?.assigned_by, entry?.created_by, entry?.actor])
      };
    })
    .filter((member: CohortMember | null): member is CohortMember => member !== null);
}

function CohortPanel({ profile, onChanged }: { profile: LLMProfile; onChanged: () => void }) {
  const [emailText, setEmailText] = useState('');
  const [busy, setBusy] = useState<'assign' | 'unassign' | ''>('');
  const [error, setError] = useState('');
  const [result, setResult] = useState<CohortResult | null>(null);
  const [members, setMembers] = useState<CohortMember[] | null>(null);
  const [membersState, setMembersState] =
    useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [removing, setRemoving] = useState<string>('');

  const profileName = profile.profile_name;

  /**
   * Who is on this candidate right now (criterion O4 — cohort testing has to be
   * inspectable, not just a count). Read-only; failure degrades to a stated
   * "unavailable", never to a silent empty roster.
   */
  const loadMembers = useCallback(async () => {
    setMembersState('loading');
    for (const suffix of COHORT_MEMBER_PATHS) {
      try {
        const response = await adminFetch(
          `${LLM_PROFILES_API}/profiles/${encodeURIComponent(profileName)}/${suffix}`
        );
        if (!response.ok) continue;
        const parsed = normalizeCohortMembers(await response.json());
        if (parsed) {
          setMembers(parsed);
          setMembersState('ready');
          return;
        }
      } catch {
        // Try the next candidate path.
      }
    }
    setMembers(null);
    setMembersState('unavailable');
  }, [profileName]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  /**
   * Remove one member. Deliberately routed through the same
   * `assign-cohort` writer the textarea uses rather than a new per-row delete:
   * one write path means one set of semantics, and this one is already proven.
   */
  const removeMember = async (email: string) => {
    setRemoving(email);
    setError('');
    try {
      const response = await adminFetch(
        `${LLM_PROFILES_API}/profiles/${encodeURIComponent(profileName)}/assign-cohort`,
        { method: 'POST', body: JSON.stringify({ emails: [email], unassign: true }) }
      );
      if (!response.ok) {
        const { message } = await readError(response);
        throw new Error(message);
      }
      setResult(null);
      await loadMembers();
      onChanged();
    } catch (err: any) {
      console.error('Failed to remove cohort member:', err);
      setError(err.message || 'Failed to remove cohort member');
    } finally {
      setRemoving('');
    }
  };

  const submit = async (unassign: boolean) => {
    // One address per line or comma-separated — accept both, the backend
    // de-duplicates and lower-cases.
    const emails = emailText
      .split(/[\n,;]+/)
      .map(e => e.trim())
      .filter(Boolean);

    if (emails.length === 0) {
      setError('Enter at least one email address.');
      return;
    }

    setBusy(unassign ? 'unassign' : 'assign');
    setError('');
    setResult(null);
    try {
      const response = await adminFetch(
        `${LLM_PROFILES_API}/profiles/${encodeURIComponent(profile.profile_name)}/assign-cohort`,
        { method: 'POST', body: JSON.stringify({ emails, unassign }) }
      );
      if (!response.ok) {
        const { message } = await readError(response);
        throw new Error(message);
      }
      setResult(await response.json());
      // The roster is the thing the admin verifies against, so it is refreshed
      // as part of the write rather than left for them to reload.
      await loadMembers();
      onChanged();
    } catch (err: any) {
      console.error('Failed to update cohort:', err);
      setError(err.message || 'Failed to update cohort');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="bg-white p-6" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
      <div className="flex items-center mb-2">
        <Users className="h-5 w-5 mr-2 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">
          Test cohort — {profile.display_name}
        </h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        {profile.is_default
          ? 'This is the live profile — every user already resolves through it, so pinning here changes nothing. Remove is still useful for clearing leftover pins.'
          : 'Named users run this candidate while everyone else stays on live. This is how a profile gets exercised before it is promoted.'}
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {result && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-sm text-blue-800">
            <strong>{result.mode === 'unassign' ? 'Removed' : 'Assigned'}:</strong>{' '}
            {result.rows_changed} of {result.requested} requested ({result.matched_users} address(es)
            matched a user).
          </p>
          {result.not_found.length > 0 && (
            <p className="text-sm text-blue-800 mt-2">
              No user found for: {result.not_found.join(', ')}
            </p>
          )}
        </div>
      )}

      {/* Current members — the roster, not just the count. A cohort you cannot
          list is a cohort you cannot verify. */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Current members
            {membersState === 'ready' && members ? ` (${members.length})` : ''}
          </h3>
          <button
            onClick={loadMembers}
            disabled={membersState === 'loading'}
            className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`h-3 w-3 ${membersState === 'loading' ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {membersState === 'loading' ? (
          <p className="text-sm text-gray-500">Loading members…</p>
        ) : membersState === 'unavailable' ? (
          <div className="border border-gray-200 rounded-md p-3" style={{ backgroundColor: '#F3F4F6' }}>
            <p className="text-sm text-gray-700">
              <strong>The member list is not available from this server.</strong> The profile
              table above still reports <strong>{profile.user_count}</strong> pinned user(s) —
              that count is real; only the names are missing here.
            </p>
          </div>
        ) : members && members.length === 0 ? (
          <p className="text-sm text-gray-500">
            Nobody is pinned to this profile yet. Add addresses below to start testing it.
          </p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  {['Email', 'Assigned', 'By', ''].map((heading, index) => (
                    <th
                      key={heading || `col-${index}`}
                      className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {(members || []).map(member => (
                  <tr key={member.email}>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {member.email}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                      {formatTimestamp(member.assigned_at)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                      {member.assigned_by || '—'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-right">
                      <button
                        onClick={() => removeMember(member.email)}
                        disabled={!!removing || !!busy}
                        className="px-3 py-1 text-xs text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={`Unpin ${member.email} from ${profile.display_name}. They fall back to the live profile.`}
                      >
                        {removing === member.email ? 'Removing…' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <label className="block text-sm font-medium text-gray-700 mb-2">
        Email addresses (one per line, or comma-separated)
      </label>
      <textarea
        value={emailText}
        onChange={(e) => setEmailText(e.target.value)}
        placeholder={'someone@example.com\nanother@example.com'}
        rows={4}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-600 focus:border-green-600 font-mono text-sm"
        disabled={!!busy}
      />
      <div className="flex items-center justify-end space-x-3 mt-3">
        <button
          onClick={() => submit(true)}
          disabled={!!busy || !emailText.trim()}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
        >
          {busy === 'unassign' ? 'Removing...' : 'Remove from cohort'}
        </button>
        <button
          onClick={() => submit(false)}
          disabled={!!busy || !emailText.trim()}
          className="flex items-center space-x-2 px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          style={{ backgroundColor: '#1F6C4F' }}
        >
          <Check className="h-4 w-4" />
          <span>{busy === 'assign' ? 'Assigning...' : 'Assign to cohort'}</span>
        </button>
      </div>
    </div>
  );
}

// ============================================
// PROMOTION HISTORY
// ============================================

/**
 * What the admin has to know before confirming a revert: has the profile they
 * are reverting TO changed since it was last live?
 *
 * Three honest states, kept distinct on purpose. "Unavailable" is not "clean" —
 * an older ledger row simply cannot answer the question, and saying so is the
 * difference between an informed revert and a hopeful one.
 */
function RevertDriftNotice({
  targetName, state, drift
}: {
  targetName: string | null;
  state: 'idle' | 'loading' | 'ready' | 'unavailable';
  drift: RevertDrift | null;
}) {
  const name = targetName || 'the restore target';

  if (state === 'idle') return null;

  if (state === 'loading') {
    return (
      <div className="border border-gray-200 rounded-md p-4">
        <p className="text-sm text-gray-600">
          Checking what has changed on <strong>{name}</strong> since it was live…
        </p>
      </div>
    );
  }

  if (state === 'unavailable' || !drift) {
    return (
      <div className="border border-gray-200 rounded-md p-4" style={{ backgroundColor: '#F3F4F6' }}>
        <p className="text-sm text-gray-700">
          <strong>Drift is unknown for this entry.</strong> Revert restores whatever{' '}
          <strong>{name}</strong> holds right now, which is not necessarily what it held
          when it was live — reverting flips the pointer, it does not roll its rows back.
        </p>
      </div>
    );
  }

  const changed = drift.calls_changed;
  const hasIssues = drift.issues.length > 0;
  const changedNames = drift.changed_calls;
  const clean =
    (changed === null || changed === 0) && changedNames.length === 0 &&
    !hasIssues && drift.ready !== false;

  if (clean) {
    return (
      <div className="rounded-md p-4" style={{ backgroundColor: '#E8F2ED', border: '1px solid #1F6C4F' }}>
        <p className="text-sm" style={{ color: '#113C2B' }}>
          <strong>{name}</strong> is unchanged since it was live
          {changed === 0 ? ' (0 calls differ)' : ''}. This revert puts back exactly what
          was running.
        </p>
        {drift.detail && (
          <p className="text-sm mt-1" style={{ color: '#113C2B' }}>{drift.detail}</p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
      <p className="text-sm font-semibold text-amber-900">
        {changed !== null
          ? `${name} has changed since it was live: ${changed} call(s) differ.`
          : `${name} has changed since it was live.`}
      </p>
      <p className="text-sm text-amber-900 mt-1">
        Reverting restores the profile as it stands today, not as it was when it was
        promoted away from.
      </p>
      {drift.detail && <p className="text-sm text-amber-900 mt-1">{drift.detail}</p>}

      {/* WHICH calls moved. A count alone says the target drifted but not
          whether it drifted anywhere the admin cares about. */}
      {changedNames.length > 0 && (
        <div className="mt-2">
          <p className="text-sm text-amber-900">Calls that differ:</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {changedNames.slice(0, 20).map(name => (
              <span
                key={name}
                className="px-2 py-0.5 text-xs font-mono rounded bg-amber-100 text-amber-900"
              >
                {name}
              </span>
            ))}
          </div>
          {changedNames.length > 20 && (
            <p className="text-sm text-amber-900 mt-1">
              …and {changedNames.length - 20} more.
            </p>
          )}
        </div>
      )}
      {changedNames.length === 0 && changed !== null && changed > 0 && (
        <p className="text-sm text-amber-900 mt-1">
          The server reported the count but not which calls — open the profile's
          configuration to see the differences.
        </p>
      )}
      {hasIssues && (
        <>
          <p className="text-sm text-amber-900 mt-2">
            Readiness against the restore target:
          </p>
          <ul className="text-sm text-amber-900 mt-1 list-disc list-inside space-y-1">
            {drift.issues.slice(0, 8).map((issue, index) => (
              <li key={`${issue.kind}-${issue.call_name || index}`}>
                {issue.severity === 'block' ? 'BLOCK — ' : ''}
                {issue.call_name ? `${issue.call_name}: ` : ''}
                {issue.detail}
              </li>
            ))}
          </ul>
          {drift.issues.length > 8 && (
            <p className="text-sm text-amber-900 mt-1">
              …and {drift.issues.length - 8} more.
            </p>
          )}
          <p className="text-sm text-amber-900 mt-2">
            Revert is deliberately not gated on these — a bad promotion must never be able
            to block its own way back. They are shown so the decision is informed.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Where drift for a ledger entry might live. Tried in order, read-only, and
 * stopping at the first parseable answer. Order matters only in that the more
 * specific name is asked for first.
 */
const REVERT_DRIFT_PATHS = ['revert-preview', 'revert-drift', 'drift'];

function PromotionHistory({ nonce, onReverted }: { nonce: number; onReverted: () => void }) {
  const [promotions, setPromotions] = useState<PromotionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmSeq, setConfirmSeq] = useState<number | null>(null);
  const [reverting, setReverting] = useState(false);
  const [drift, setDrift] = useState<RevertDrift | null>(null);
  const [driftState, setDriftState] = useState<'idle' | 'loading' | 'ready' | 'unavailable'>('idle');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminFetch(`${LLM_PROFILES_API}/promotions?limit=50`);
      if (!response.ok) {
        const { message } = await readError(response);
        throw new Error(message);
      }
      const data = await response.json();
      setPromotions(data.promotions || []);
    } catch (err: any) {
      console.error('Failed to fetch promotion history:', err);
      setError(err.message || 'Failed to fetch promotion history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, nonce]);

  /**
   * Revert restores the POINTER, not the rows. If the outgoing profile was
   * edited after it was demoted, reverting lands on the edited version — so how
   * far it has drifted has to be on screen before the admin confirms, not
   * discovered afterwards.
   *
   * Two sources, in order: drift the server attached to the ledger row, then a
   * preview fetch. Neither is assumed to exist — rows written before drift
   * tracking carry nothing, and that is reported as "not available" rather than
   * quietly rendered as "no drift".
   */
  useEffect(() => {
    if (confirmSeq === null) {
      setDrift(null);
      setDriftState('idle');
      return;
    }

    const inline = normalizeRevertDrift(promotions.find(p => p.seq === confirmSeq));
    if (inline) {
      setDrift(inline);
      setDriftState('ready');
      return;
    }

    let cancelled = false;
    setDrift(null);
    setDriftState('loading');
    (async () => {
      // Read-only, and asked for BEFORE the revert runs — the whole point is
      // that the admin sees the drift while they can still decline. Two
      // candidate paths because the endpoint is newer than this file; the first
      // that answers with something parseable wins, and neither existing is a
      // supported state, not an error.
      for (const suffix of REVERT_DRIFT_PATHS) {
        if (cancelled) return;
        try {
          const response = await adminFetch(
            `${LLM_PROFILES_API}/promotions/${confirmSeq}/${suffix}`
          );
          if (!response.ok) continue;
          const parsed = normalizeRevertDrift(await response.json());
          if (cancelled) return;
          if (parsed) {
            setDrift(parsed);
            setDriftState('ready');
            return;
          }
        } catch {
          // Network/parse failure on one candidate: try the next.
        }
      }
      // Absence is expected on older ledger rows and on any build whose server
      // does not compute a preview. It must never block the revert — this is
      // the emergency exit.
      if (cancelled) return;
      setDrift(null);
      setDriftState('unavailable');
    })();

    return () => { cancelled = true; };
  }, [confirmSeq, promotions]);

  const handleRevert = async (seq: number) => {
    setReverting(true);
    setError('');
    try {
      const response = await adminFetch(`${LLM_PROFILES_API}/promotions/${seq}/revert`, {
        method: 'POST'
      });
      if (!response.ok) {
        const { message } = await readError(response);
        throw new Error(message);
      }
      setConfirmSeq(null);
      // onReverted bumps `nonce`, which re-runs the effect below and reloads
      // this table — no second fetch needed here.
      onReverted();
    } catch (err: any) {
      console.error('Failed to revert promotion:', err);
      setError(err.message || 'Failed to revert promotion');
    } finally {
      setReverting(false);
    }
  };

  // The backend only permits reverting the most recent ledger entry — an older
  // one would restore a configuration that later changes were built on top of.
  const newest = promotions[0];
  const revertibleSeq = newest && newest.action === 'promote' ? newest.seq : null;
  const revertTarget = confirmSeq !== null
    ? promotions.find(p => p.seq === confirmSeq) || null
    : null;

  return (
    <div className="bg-white p-6" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <History className="h-5 w-5 mr-2 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Promotion history</h2>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center space-x-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {loading && promotions.length === 0 ? (
        <p className="text-sm text-gray-500">Loading history...</p>
      ) : promotions.length === 0 ? (
        <p className="text-sm text-gray-500">
          No promotions yet. The ledger records every flip of the live profile.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                {['#', 'When', 'Who', 'Change', 'Users', 'Actions'].map(heading => (
                  <th
                    key={heading}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {promotions.map(entry => (
                <tr key={entry.seq}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{entry.seq}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatTimestamp(entry.created_at)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {entry.promoted_by}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <span className={`px-2 py-0.5 mr-2 text-xs font-medium rounded ${
                      entry.action === 'revert'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {entry.action}
                    </span>
                    <span className="font-mono text-xs">
                      {entry.from_profile} → {entry.to_profile}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {countOf(entry.users_affected)}
                    {entry.pins_deleted > 0 && (
                      <span className="text-xs text-gray-500 ml-1">
                        ({entry.pins_deleted} pins cleared)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {entry.seq === revertibleSeq ? (
                      <button
                        onClick={() => setConfirmSeq(entry.seq)}
                        className="flex items-center space-x-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                      >
                        <RotateCcw className="h-3 w-3" />
                        <span>Revert</span>
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Revert confirmation */}
      {revertTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <RotateCcw className="h-6 w-6 mr-2 text-amber-600" />
                <h2 className="text-xl font-semibold text-gray-900">Revert promotion</h2>
              </div>
              <button
                onClick={() => setConfirmSeq(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700">
                This puts <strong>{revertTarget.from_profile}</strong> back as the live
                profile, undoing entry #{revertTarget.seq} (
                {revertTarget.from_profile} → {revertTarget.to_profile}).
              </p>

              <RevertDriftNotice
                targetName={revertTarget.from_profile}
                state={driftState}
                drift={drift}
              />

              <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                <p className="text-sm text-amber-900">
                  The pins deleted by the original promotion are <strong>not</strong>{' '}
                  restored. That includes any cohort pins that pointed at the promoted
                  profile: recreating them would leave those users on the configuration
                  just judged bad. Everyone follows the restored profile.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setConfirmSeq(null)}
                disabled={reverting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRevert(revertTarget.seq)}
                disabled={reverting}
                className="flex items-center space-x-2 px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                style={{ backgroundColor: '#B45309' }}
              >
                {reverting ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>Reverting...</span>
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4" />
                    <span>Revert to {revertTarget.from_profile}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Create Profile Modal Component
 */
interface CreateProfileModalProps {
  profiles: LLMProfile[];
  onClose: () => void;
  onProfileCreated: () => void;
}

function CreateProfileModal({ profiles, onClose, onProfileCreated }: CreateProfileModalProps) {
  const [profileName, setProfileName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  // Default the clone source to the LIVE profile, not the profile NAMED
  // 'default'. Under the pointer-flip promotion model those are different
  // things the moment a promotion happens: after promoting Terra, 'default'
  // is the DEMOTED old configuration — and a new candidate silently cloned
  // from it would be born months behind the fleet. (Pre-fix this select
  // hardcoded exactly two options, 'default' and empty, so cloning the live
  // profile post-promote was not even possible from the UI.)
  const liveProfile = profiles.find(p => p.is_default);
  const [copyFromProfile, setCopyFromProfile] = useState(
    liveProfile?.profile_name ?? 'default'
  );
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    // Validation
    if (!profileName.trim()) {
      setError('Profile name is required');
      return;
    }
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    // Profile name validation (lowercase, underscores only)
    if (!/^[a-z0-9_]+$/.test(profileName)) {
      setError('Profile name must be lowercase letters, numbers, and underscores only');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const response = await adminFetch(`${LLM_PROFILES_API}/profiles`, {
        method: 'POST',
        body: JSON.stringify({
          profile_name: profileName,
          display_name: displayName,
          description: description,
          copy_from_profile: copyFromProfile
        })
      });

      if (!response.ok) {
        const { message } = await readError(response);
        throw new Error(message);
      }

      onProfileCreated();
    } catch (err: any) {
      console.error('Failed to create profile:', err);
      setError(err.message || 'Failed to create profile');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Plus className="h-6 w-6 mr-2 text-green-700" />
            <h2 className="text-xl font-semibold text-gray-900">Create New LLM Profile</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Profile Name (Internal) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Name (Internal Identifier)
            </label>
            <input
              type="text"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value.toLowerCase())}
              placeholder="e.g., fast_mode, high_quality, cost_optimized"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-600 focus:border-green-600"
              disabled={creating}
            />
            <p className="text-xs text-gray-500 mt-1">
              Lowercase letters, numbers, and underscores only. Cannot be changed after creation.
            </p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., Fast Mode, High Quality, Cost Optimized"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-600 focus:border-green-600"
              disabled={creating}
            />
            <p className="text-xs text-gray-500 mt-1">
              User-friendly name shown in dropdowns and UI.
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the purpose of this profile (e.g., 'Optimized for speed with GPT-5-mini for all calls' or 'A/B test with Claude for intelligence engines')"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-600 focus:border-green-600"
              disabled={creating}
            />
          </div>

          {/* Copy From Profile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Copy Configuration From
            </label>
            <select
              value={copyFromProfile}
              onChange={(e) => setCopyFromProfile(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-600 focus:border-green-600"
              disabled={creating}
            >
              {profiles.map(p => (
                <option key={p.profile_name} value={p.profile_name}>
                  {p.display_name}{p.is_default ? ' (live — what users run today)' : ''}
                </option>
              ))}
              <option value="">Start with empty configuration</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              New profile will copy every LLM call configuration from the selected
              profile — including active/inactive state, byte-for-byte. Defaults to
              the live profile so a new candidate starts from what users actually
              run today. You can then modify individual calls.
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-800">
              <strong>What happens next:</strong> After creating the profile, you can edit individual LLM call configurations below.
              Assign a test cohort, run the readiness check, then promote it when you are ready.
            </p>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={creating}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || !profileName.trim() || !displayName.trim()}
            className="flex items-center space-x-2 px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: '#1F6C4F' }}
          >
            {creating ? (
              <>
                <span className="animate-spin">⏳</span>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                <span>Create Profile</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
